import {
  CalendarIcon,
  ChevronDown,
  Database,
  Download,
  Eye,
  FileText,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import {
  createHotspot as createLocalHotspot,
  removeHotspot as removeLocalHotspot,
  updateHotspot as updateLocalHotspot,
} from "@/data/adapters/local/hotspots";
import { updateProject as updateLocalProject, getProjectCraftspaceId } from "@/data/adapters/local/projects";
import { updatePanorama as updateLocalPanorama } from "@/data/adapters/local/panoramas";
import { convex } from "@/lib/convexClient";
import { localDb } from "@/lib/localDb";
import { type CMSDataRecord, CMSService } from "@/services/cmsService";
import { setTextVariableCustomFields } from "@/components/editor/textVariables";
import { setCMSRowCache } from "@/components/editor/resolveTextVariables";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import { type Hotspot, type Panorama, useEditorStore } from "@/store/editorStore";
import {
  HOTSPOT_STYLE_PRESET_ID_KEY,
  mergeContentWithPreset,
  type HotspotStylePreset,
} from "@/components/editor/PropertiesPanel";
import { api } from "../../convex/_generated/api";
import {
  type DateFieldOptions,
  DEFAULT_DATE_OPTIONS,
  DateFieldEditor,
  formatDateValue,
} from "@/components/cms/DateFieldEditor";

interface FieldStructure {
  name: string;
  displayName: string;
  fieldType: string;
  isRequired?: boolean;
  options?: string[];
  defaultValue?: string;
  dateOptions?: DateFieldOptions;
}

type CustomFieldUndoAction =
  | {
      type: "add";
      field: FieldStructure;
      index: number;
    }
  | {
      type: "delete";
      field: FieldStructure;
      index: number;
      values: Array<unknown>;
      width?: number;
    };

interface DataRow {
  [key: string]: string | number | boolean | null | undefined;
}

interface CopiedPanorama {
  id: string;
  name: string;
  thumbnail: string;
}

const PANORAMAS_COLLECTION_NAME = "Panoramas";
const HOTSPOTS_COLLECTION_NAME = "Hotspots";
const LEGACY_PANORAMAS_COLLECTION_NAME = "Panoramas Collection";
const LEGACY_HOTSPOTS_COLLECTION_NAME = "Hotspots Collection";
const PANORAMAS_BASE_FIELD_NAMES = new Set([
  "id",
  "name",
  "panorama_file",
  "file_url",
  "file_url_webp",
  "thumbnail_url",
  "thumbnail_url_webp",
  "width",
  "height",
  "file_size",
  "order",
  "file_status",
  "processing_status",
  "private_file_url",
  "coordinates",
  "created_at",
]);
const HOTSPOTS_BASE_FIELD_NAMES = new Set([
  "id",
  "name",
  "type",
  "panorama_id",
  "panorama_name",
  "panorama_file",
  "hotspot_style",
  "visible",
  "order",
  "position_x",
  "position_y",
  "position_z",
  "rotation_x",
  "rotation_y",
  "rotation_z",
  "scale_x",
  "scale_y",
  "scale_z",
  "content",
  "cms_action",
  "created_at",
  "updated_at",
]);

const orderCMSRecords = (records: CMSDataRecord[]) => {
  const getPriority = (name: string) => {
    if (name === HOTSPOTS_COLLECTION_NAME || name === LEGACY_HOTSPOTS_COLLECTION_NAME) {
      return 0;
    }
    if (name === PANORAMAS_COLLECTION_NAME || name === LEGACY_PANORAMAS_COLLECTION_NAME) {
      return 1;
    }
    return 2;
  };

  return records
    .map((record, index) => ({ record, index, priority: getPriority(record.name) }))
    .sort((a, b) => a.priority - b.priority || a.index - b.index)
    .map(({ record }) => record);
};

const CMS = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const { toast } = useToast();

  // Get state from editor store
  const { project, projectBackend, setProject } = useEditorStore();
  const isLocalBackend = isElectron() && projectBackend === "local";
  const userId = user?.id ?? "local";

  const [cmsRecords, setCmsRecords] = useState<CMSDataRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<CMSDataRecord | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Import form state
  const [importName, setImportName] = useState("");
  const [importDescription, setImportDescription] = useState("");

  // Load project from URL if not already in store
  useEffect(() => {
    if (!project && projectId) {
      const loadProject = async () => {
        try {
          if (isLocalBackend) {
            const craftspaceId = await getProjectCraftspaceId(projectId);
            if (craftspaceId) {
              const localProject = await localDb(craftspaceId).get(
                "SELECT id as _id, * FROM projects WHERE id = ? OR public_id = ?",
                [projectId, projectId],
              );
              if (localProject) {
                setProject({
                  ...localProject,
                  id: localProject._id,
                } as any);
              }
            }
          } else {
            const convexProject = await convex.query(api.projects.get, { id: projectId });
            if (convexProject) {
              setProject(convexProject as any);
            }
          }
        } catch (error) {
          console.error("Failed to load project:", error);
        }
      };
      loadProject();
    }
  }, [projectId, project, isLocalBackend, setProject]);

  // Redirect if not authenticated (Electron local mode bypasses auth)
  if (!user && !isElectron()) {
    return <Navigate to="/login" replace />;
  }

  if (!project) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-800"
      >
        <div className="text-white">Loading project...</div>
      </div>
    );
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !projectId || !project?.craftspace_id) return;
    if (!user && !isLocalBackend) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please select a CSV file.",
      });
      return;
    }

    setIsUploading(true);
    try {
      const craftspaceId = project.craftspace_id;

      const record = await CMSService.importCsvFile(file, projectId, userId, craftspaceId, {
        name: importName || file.name.replace(/\.csv$/i, ""),
        description: importDescription || undefined,
      });

      // Add to local state
      setCmsRecords((prev) => [record, ...prev]);

      // Reset form
      setImportName("");
      setImportDescription("");

      toast({
        title: "CSV imported successfully",
        description: `Imported ${record.rowCount} items with ${record.fieldCount} fields as JSON.`,
      });
    } catch (error) {
      console.error("Error importing CSV:", error);
      toast({
        variant: "destructive",
        title: "Import failed",
        description: error instanceof Error ? error.message : "Failed to import CSV file.",
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleViewData = async (record: CMSDataRecord) => {
    if (!project?.craftspace_id) return;

    try {
      setIsActiveLoading(true);
      setActiveRecordId(record.id);
      const recordWithData = await CMSService.loadCMSDataWithContent(
        record.id,
        project.craftspace_id,
      );
      setActiveRecord(recordWithData);
    } catch (error) {
      console.error("Error loading CMS data content:", error);
      toast({
        variant: "destructive",
        title: "Failed to load data",
        description: error instanceof Error ? error.message : "Could not load JSON data",
      });
    } finally {
      setIsActiveLoading(false);
    }
  };

  const handleExportData = async (record: CMSDataRecord) => {
    if (!project?.craftspace_id) return;

    try {
      let recordWithData = record;

      // Load data if not already loaded
      if (!record.data) {
        recordWithData = await CMSService.loadCMSDataWithContent(record.id, project.craftspace_id);
      }

      await CMSService.exportAsCSV(recordWithData);

      toast({
        title: "Export successful",
        description: "CSV file has been downloaded.",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: error instanceof Error ? error.message : "Failed to export data.",
      });
    }
  };

  const handleDeleteRecord = async (recordId: string) => {
    if (!project?.craftspace_id) return;

    try {
      await CMSService.deleteCMSData(recordId, project.craftspace_id);

      // Remove from local state
      setCmsRecords((prev) => prev.filter((r) => r.id !== recordId));

      toast({
        title: "Data deleted",
        description: "CMS data has been permanently deleted.",
      });
    } catch (error) {
      console.error("Error deleting CMS data:", error);
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete data.",
      });
    }
  };

  const handleRenameRecord = async (record: CMSDataRecord) => {
    const nextName = window.prompt("Rename table", record.name);
    if (!nextName) return;
    const trimmed = nextName.trim();
    if (!trimmed || trimmed === record.name) return;

    try {
      const updated = await CMSService.renameDataset(record.id, trimmed);
      setCmsRecords((prev) => prev.map((r) => (r.id === record.id ? { ...r, name: updated.name } : r)));
      if (activeRecordId === record.id) {
        setActiveRecord((prev) => (prev ? { ...prev, name: updated.name } : prev));
      }
      toast({
        title: "Table renamed",
        description: `Renamed to "${updated.name}".`,
      });
    } catch (error) {
      console.error("Error renaming CMS table:", error);
      toast({
        variant: "destructive",
        title: "Rename failed",
        description: error instanceof Error ? error.message : "Failed to rename table.",
      });
    }
  };

  const commitHotspotNameEditPanel = async (valueOverride?: string) => {
    if (!editingCell || !isHotspotsTable) return;
    const row = activeRows[editingCell.rowIndex];
    const hotspotId = String(row?.id ?? "");
    const isDraft = Boolean((row as { __draft?: boolean })?.__draft);

    const nextName = (valueOverride ?? editingCell.value).trim();
    const currentName = typeof row?.name === "string" ? row.name : "";
    if (!nextName || nextName === currentName) {
      setEditingCell(null);
      return;
    }

    if (isDraft || !hotspotId) {
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        rows[editingCell.rowIndex] = {
          ...rows[editingCell.rowIndex],
          name: nextName,
        } as Record<string, unknown>;
        return { ...prev, data: { ...prev.data, rows } };
      });
      setEditingCell(null);
      return;
    }

    try {
      if (!user && !isLocalBackend) {
        throw new Error("You must be signed in to edit hotspot names.");
      }

      if (isLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, name: nextName });
      } else {
        await convex.mutation(api.hotspots.update, { id: hotspotId, name: nextName });
      }

      updateHotspot(hotspotId, { name: nextName });
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        rows[editingCell.rowIndex] = {
          ...rows[editingCell.rowIndex],
          name: nextName,
        } as Record<string, unknown>;
        return { ...prev, data: { ...prev.data, rows } };
      });
    } catch (error) {
      console.error("Error updating hotspot name:", error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update hotspot name.",
      });
    } finally {
      setEditingCell(null);
    }
  };


  const getFieldTypeColor = (fieldType: string) => {
    switch (fieldType) {
      case "text":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "hotspot_style":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "longtext":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "number":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "url":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case "date":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "switch":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  const orderedCmsRecords = useMemo(() => orderCMSRecords(cmsRecords), [cmsRecords]);

  return (
    <div className="flex flex-col bg-gray-800">
      <div className="flex-1 overflow-auto">
        <div className="max-w-4xl mx-auto py-4 px-4 pt-8">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Database className="h-8 w-8 text-white" />
            <div>
              <h1 className="text-2xl font-bold text-white">Content Management System</h1>
              <p className="text-gray-300">Import CSV files and manage structured data as JSON</p>
            </div>
          </div>

          <div className="space-y-8">
            {/* Import Section */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  Import CSV Data
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Upload a CSV file to convert and store as JSON data
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="importName" className="text-gray-300">
                      Dataset Name
                    </Label>
                    <Input
                      id="importName"
                      value={importName}
                      onChange={(e) => setImportName(e.target.value)}
                      placeholder="Enter dataset name"
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                    />
                  </div>
                  <div>
                    <Label htmlFor="importDescription" className="text-gray-300">
                      Description (Optional)
                    </Label>
                    <Textarea
                      id="importDescription"
                      value={importDescription}
                      onChange={(e) => setImportDescription(e.target.value)}
                      placeholder="Describe this dataset"
                      items={2}
                      className="bg-gray-700 border-gray-600 text-white placeholder:text-gray-400"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="csvFile" className="text-gray-300">
                    CSV File
                  </Label>
                  <Input
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    disabled={isUploading}
                    className="bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:border-gray-500 file:text-white"
                  />
                  <p className="text-sm text-gray-400 mt-1">
                    CSV files will be transformed to JSON format for faster loading
                  </p>
                </div>

                {isUploading && (
                  <div className="flex items-center gap-2 text-blue-400">
                    <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Converting CSV to JSON and uploading...</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CMS Data List */}
            {isLoading ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-2 text-blue-400">
                    <div className="w-6 h-6 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                    <span>Loading CMS data...</span>
                  </div>
                </CardContent>
              </Card>
            ) : cmsRecords.length > 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Stored Datasets ({cmsRecords.length})
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Manage your imported JSON datasets
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {cmsRecords.map((record) => (
                      <div
                        key={record.id}
                        className="flex items-center justify-between p-4 bg-gray-700 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-white font-medium">{record.name}</h3>
                            <Badge variant="outline" className="text-green-400 border-green-400">
                              JSON
                            </Badge>
                          </div>

                          {record.description && (
                            <p className="text-gray-400 text-sm mb-2">{record.description}</p>
                          )}

                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span>{record.rowCount} items</span>
                            <span>{record.fieldCount} fields</span>
                            <span>{formatFileSize(record.fileSize)}</span>
                            <span>{new Date(record.createdAt).toLocaleDateString()}</span>
                          </div>

                          {record.importErrors.length > 0 && (
                            <div className="mt-2">
                              <Badge
                                variant="outline"
                                className="text-yellow-400 border-yellow-400"
                              >
                                {record.importErrors.length} import warnings
                              </Badge>
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewData(record)}
                            className="text-blue-400 hover:text-blue-300 hover:bg-blue-400/10"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleExportData(record)}
                            className="text-green-400 hover:text-green-300 hover:bg-green-400/10"
                          >
                            <Download className="h-4 w-4" />
                          </Button>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-400/10"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-gray-800 border-gray-700">
                              <AlertDialogHeader>
                                <AlertDialogTitle className="text-white">
                                  Delete Dataset
                                </AlertDialogTitle>
                                <AlertDialogDescription className="text-gray-400">
                                  This will permanently delete "{record.name}" and all its data.
                                  This action cannot be undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600">
                                  Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteRecord(record.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete Dataset
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Database className="h-16 w-16 text-gray-500 mb-4" />
                  <h3 className="text-white text-lg font-medium mb-2">No Datasets Imported</h3>
                  <p className="text-gray-400 text-center mb-4">
                    Import a CSV file to create your first JSON dataset
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import CSV File
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      {/* View Data Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-6xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedRecord?.name} - Data Preview</DialogTitle>
            <DialogDescription className="text-gray-400">
              Showing first 10 items of {selectedRecord?.rowCount} total items
            </DialogDescription>
          </DialogHeader>

          {selectedRecord?.data && (
            <div className="overflow-auto">
              <div className="mb-4">
                <h4 className="text-white font-medium mb-2">Field Structure</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRecord.fieldStructure.map((field: FieldStructure) => (
                    <Badge key={field.name} className={getFieldTypeColor(field.fieldType)}>
                      {field.displayName} ({field.fieldType})
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-600">
                      {selectedRecord.fieldStructure.map((field: FieldStructure) => (
                        <TableHead key={field.name} className="text-gray-300">
                          {field.displayName}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedRecord.data?.rows || [])
                      .slice(0, 10)
                      .map((row: DataRow, index: number) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: Stable list of first 10 items in preview
                        <TableRow key={index} className="border-gray-600">
                          {selectedRecord.fieldStructure.map((field: FieldStructure) => (
                            <TableCell key={field.name} className="text-gray-300">
                              <div className="max-w-xs truncate">
                                {(() => {
                                  const value = row[field.name];
                                  if (value === null || value === undefined || value === "") {
                                    return "-";
                                  }
                                  if (field.fieldType === "date" || field.fieldType === "datetime") {
                                    const formatted = formatDateValue(value, field.dateOptions);
                                    return formatted || value;
                                  }
                                  return value;
                                })()}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>

              {(selectedRecord.data?.rows || []).length > 10 && (
                <p className="text-gray-400 text-sm mt-2 text-center">
                  ... and {(selectedRecord.data?.rows || []).length - 10} more items
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMS;

// Panel component for use within ProjectCraftspace
export const CMSPanel = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  // Get state from editor store (already loaded by ProjectCraftspace)
  const {
    project,
    hotspots,
    panoramas,
    projectBackend,
    updateHotspot,
    updatePanorama,
    addHotspot,
    deleteHotspot,
    setSelectedHotspot,
    requestFocusHotspot,
    setSelectedPanorama,
    hotspotRenderStyle,
    setProject,
  } = useEditorStore();
  const isLocalBackend = isElectron() && projectBackend === "local";
  const userId = user?.id ?? "local";

  const [cmsRecords, setCmsRecords] = useState<CMSDataRecord[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<CMSDataRecord | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const updateFileInputRef = useRef<HTMLInputElement>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const attemptedHotspotsRefresh = useRef(false);
  const suppressHotspotRefreshRef = useRef(false);
  const attemptedCMSResyncRef = useRef(false);
  const pendingPanoramaSelectRef = useRef<string | null>(null);
  const copiedPanoramaRef = useRef<CopiedPanorama | null>(null);
  const copiedHotspotStyleRef = useRef<string | null>(null);
  const lastClearActionRef = useRef<{
    rowId: string;
    rowIndex: number;
    fieldName: "name" | "panorama_file" | "hotspot_style";
    prevValues: Record<string, unknown>;
    prevContent?: Record<string, unknown>;
  } | null>(null);
  const [activeRecordId, setActiveRecordId] = useState<string | null>(null);
  const [activeRecord, setActiveRecord] = useState<CMSDataRecord | null>(null);
  const [isActiveLoading, setIsActiveLoading] = useState(false);
  const [activeRecordRefreshKey, setActiveRecordRefreshKey] = useState(0);
  const [showAddImport, setShowAddImport] = useState(false);
  const [selectedCell, setSelectedCell] = useState<{ rowIndex: number; fieldName: string } | null>(
    null,
  );
  const [editingCell, setEditingCell] = useState<{
    rowIndex: number;
    fieldName: string;
    value: string;
  } | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());
  const lastSelectedRowIndexRef = useRef<number | null>(null);
  const [showAllFields, setShowAllFields] = useState(false);
  const [isTableMenuOpen, setIsTableMenuOpen] = useState(false);
  const [contentPopover, setContentPopover] = useState<{
    rowIndex: number;
    fieldName: string;
  } | null>(null);
  const [hotspotStylePopoverRow, setHotspotStylePopoverRow] = useState<number | null>(null);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const customFieldUndoStackRef = useRef<CustomFieldUndoAction[]>([]);
  const columnResizeRef = useRef<{
    fieldName: string;
    startX: number;
    startWidth: number;
  } | null>(null);
  const columnWidthsStorageKey = useMemo(
    () => (projectId && activeRecordId ? `cms_column_widths_${projectId}_${activeRecordId}` : null),
    [activeRecordId, projectId],
  );
  const [addFieldDialogOpen, setAddFieldDialogOpen] = useState(false);
  const [newFieldName, setNewFieldName] = useState("");
  const [newFieldDisplayName, setNewFieldDisplayName] = useState("");
  const [newFieldType, setNewFieldType] = useState("longtext");
  const [newDateOptions, setNewDateOptions] = useState<DateFieldOptions>({ ...DEFAULT_DATE_OPTIONS });
  const [newFieldOptions, setNewFieldOptions] = useState<string[]>([]);
  const [newFieldOptionInput, setNewFieldOptionInput] = useState("");
  const [isSavingCustomField, setIsSavingCustomField] = useState(false);
  const [editFieldDialogOpen, setEditFieldDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<FieldStructure | null>(null);
  const [editFieldDateOptions, setEditFieldDateOptions] = useState<DateFieldOptions>({ ...DEFAULT_DATE_OPTIONS });
  const [editFieldOptions, setEditFieldOptions] = useState<string[]>([]);
  const [editFieldOptionInput, setEditFieldOptionInput] = useState("");
  const [editFieldDefaultValue, setEditFieldDefaultValue] = useState<string>("");

  // Import form state
  const [_importName, setImportName] = useState("");
  const [importDescription, setImportDescription] = useState("");

  // Update form state
  const [updateRecord, setUpdateRecord] = useState<CMSDataRecord | null>(null);
  const [mergeField, setMergeField] = useState("");
  const [updateExisting, setUpdateExisting] = useState(true);
  const [addNew, setAddNew] = useState(true);

  const formatCellValue = useCallback((value: unknown): string => {
    if (value === null || value === undefined) return "";
    if (typeof value === "boolean") return value ? "true" : "false";
    if (typeof value === "number") return value.toString();
    if (typeof value === "string") {
      return value.length > 120 ? `${value.slice(0, 120)}…` : value;
    }
    try {
      const stringified = JSON.stringify(value);
      return stringified.length > 120 ? `${stringified.slice(0, 120)}…` : stringified;
    } catch {
      return String(value);
    }
  }, []);

  const sanitizeFieldName = useCallback((value: string) => {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_]+/g, "_")
      .replace(/^_+|_+$/g, "");
    return normalized;
  }, []);

  const toDisplayName = useCallback((value: string) => {
    if (!value) return "";
    return value
      .split("_")
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  }, []);

  const isCustomField = useCallback((field: FieldStructure, recordName?: string | null) => {
    if (!recordName) return false;
    if (
      recordName === HOTSPOTS_COLLECTION_NAME ||
      recordName === LEGACY_HOTSPOTS_COLLECTION_NAME
    ) {
      return !HOTSPOTS_BASE_FIELD_NAMES.has(field.name);
    }
    if (
      recordName === PANORAMAS_COLLECTION_NAME ||
      recordName === LEGACY_PANORAMAS_COLLECTION_NAME
    ) {
      return !PANORAMAS_BASE_FIELD_NAMES.has(field.name);
    }
    return false;
  }, []);

  // Sync custom CMS fields and row data to the text variable system
  useEffect(() => {
    const custom: Record<string, { name: string; description: string }[]> = {};
    const hotspotRows = new Map<string, Record<string, unknown>>();
    const panoramaRows = new Map<string, Record<string, unknown>>();

    for (const record of cmsRecords) {
      const fields: FieldStructure[] =
        (record as { data?: { fields?: FieldStructure[] } }).data?.fields ??
        ((record.fieldStructure ?? []) as FieldStructure[]);
      const rName = record.name;
      const isHotspots =
        rName === HOTSPOTS_COLLECTION_NAME || rName === LEGACY_HOTSPOTS_COLLECTION_NAME;
      const isPanoramas =
        rName === PANORAMAS_COLLECTION_NAME || rName === LEGACY_PANORAMAS_COLLECTION_NAME;
      if (!isHotspots && !isPanoramas) continue;

      const baseFields = isHotspots ? HOTSPOTS_BASE_FIELD_NAMES : PANORAMAS_BASE_FIELD_NAMES;
      const key = isHotspots ? "hotspots" : "panoramas";
      const customFields = fields
        .filter((f) => !baseFields.has(f.name))
        .map((f) => ({ name: f.name, description: f.displayName || f.name }));
      if (customFields.length > 0) {
        custom[key] = [...(custom[key] ?? []), ...customFields];
      }

      // Cache row data for resolver
      const rows =
        ((record as { data?: { rows?: Array<Record<string, unknown>> } }).data?.rows) ?? [];
      for (const row of rows) {
        const id = typeof row.id === "string" ? row.id : String(row.id ?? "");
        if (!id) continue;
        if (isHotspots) hotspotRows.set(id, row);
        else panoramaRows.set(id, row);
      }
    }

    setTextVariableCustomFields(custom);
    setCMSRowCache(hotspotRows, panoramaRows);
  }, [cmsRecords]);

  const getDefaultColumnWidth = useCallback(
    (field: FieldStructure, recordName?: string | null) => {
      if (
        (recordName === HOTSPOTS_COLLECTION_NAME ||
          recordName === LEGACY_HOTSPOTS_COLLECTION_NAME ||
          recordName === PANORAMAS_COLLECTION_NAME ||
          recordName === LEGACY_PANORAMAS_COLLECTION_NAME) &&
        field.name === "name"
      ) {
        return 320;
      }
      if (recordName && (recordName === HOTSPOTS_COLLECTION_NAME || recordName === LEGACY_HOTSPOTS_COLLECTION_NAME)) {
        if (field.name === "hotspot_style") return 200;
        if (field.name === "panorama_file") return 260;
      }
      return 180;
    },
    [],
  );

  const coerceFieldValue = useCallback((value: string, fieldType: string): unknown => {
    if (value === "") return null;
    switch (fieldType) {
      case "number":
      case "integer": {
        const parsed = Number(value);
        return Number.isNaN(parsed) ? null : parsed;
      }
      case "switch":
        return value === "true";
      case "date":
      case "datetime":
        return value;
      default:
        return value;
    }
  }, []);

  const handleResizeStart = useCallback(
    (fieldName: string, event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const headerCell = event.currentTarget.parentElement as HTMLElement | null;
      const startWidth = headerCell?.offsetWidth ?? columnWidths[fieldName] ?? 160;
      columnResizeRef.current = {
        fieldName,
        startX: event.clientX,
        startWidth,
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!columnResizeRef.current) return;
        const delta = moveEvent.clientX - columnResizeRef.current.startX;
        const nextWidth = Math.max(120, Math.min(600, columnResizeRef.current.startWidth + delta));
        setColumnWidths((prev) => ({
          ...prev,
          [columnResizeRef.current?.fieldName ?? fieldName]: nextWidth,
        }));
      };

      const handleMouseUp = () => {
        columnResizeRef.current = null;
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("mouseup", handleMouseUp);
      };

      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [columnWidths],
  );

  const parseRowContent = useCallback((row: Record<string, unknown>) => {
    const content = (row as { content?: unknown }).content;
    if (typeof content === "string") {
      try {
        return JSON.parse(content) as Record<string, unknown>;
      } catch {
        return {};
      }
    }
    if (typeof content === "object" && content !== null) {
      return content as Record<string, unknown>;
    }
    return {};
  }, []);

  const stylePresetStorageKey = useMemo(
    () => `hotspotStylePresets:${project?.id || "global"}`,
    [project?.id],
  );
  const sanitizeStylePresets = useCallback(
    (value: unknown) =>
      Array.isArray(value)
        ? value.filter(
            (item): item is HotspotStylePreset =>
              item &&
              typeof item === "object" &&
              typeof item.id === "string" &&
              typeof item.name === "string" &&
              typeof item.hotspotType === "string" &&
              item.content &&
              typeof item.content === "object",
          )
        : [],
    [],
  );

  const projectStylePresets = useMemo(
    () =>
      sanitizeStylePresets(
        (project?.data as Record<string, unknown> | null | undefined)?.hotspotStylePresets,
      ),
    [project?.data, sanitizeStylePresets],
  );

  useEffect(() => {
    if (!project || projectStylePresets.length > 0) return;
    const raw = localStorage.getItem(stylePresetStorageKey);
    if (!raw) return;
    const migrate = async () => {
      try {
        const parsed = JSON.parse(raw);
        const sanitized = sanitizeStylePresets(parsed);
        if (sanitized.length === 0) return;
        const currentData =
          (project.data as Record<string, unknown> | null | undefined) ?? {};
        const nextData = { ...currentData, hotspotStylePresets: sanitized };
        if (isLocalBackend) {
          await updateLocalProject({ id: project.id, data: nextData });
        } else {
          await convex.mutation(api.projects.update, { id: project.id, data: nextData });
        }
        setProject({ ...project, data: nextData });
        localStorage.removeItem(stylePresetStorageKey);
      } catch {
        // ignore migration failures
      }
    };
    void migrate();
  }, [
    convex,
    isLocalBackend,
    project,
    projectStylePresets.length,
    sanitizeStylePresets,
    setProject,
    stylePresetStorageKey,
  ]);

  const loadHotspotStylePresets = useCallback((): HotspotStylePreset[] => {
    if (projectStylePresets.length > 0) return projectStylePresets;
    const raw = localStorage.getItem(stylePresetStorageKey);
    if (!raw) return [];
    try {
      return sanitizeStylePresets(JSON.parse(raw));
    } catch {
      return [];
    }
  }, [projectStylePresets, sanitizeStylePresets, stylePresetStorageKey]);
  const hotspotStylePresets = useMemo(() => loadHotspotStylePresets(), [loadHotspotStylePresets]);
  const hotspotStyleNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const preset of hotspotStylePresets) {
      map.set(preset.id, preset.name);
    }
    return map;
  }, [hotspotStylePresets]);

  const defaultHtmlHotspotContent = useMemo(
    () => ({
      text: "",
      textColor: "#000000",
      backgroundColor: "rgba(255, 255, 255, 0.9)",
      borderColor: "#ffffff",
      borderWidth: 0,
      borderRadius: 0,
      padding: 0,
      fontSize: "3px",
      fontWeight: "500",
      textAlign: "center",
      lineHeight: "1",
      textShadow: "none",
      variant: "default",
      showPointer: false,
      pointerDirection: "bottom",
      pointerSize: 8,
      pointerOverlap: 2,
      tooltipArrowDirection: "top",
      renderStyle: hotspotRenderStyle,
    }),
    [hotspotRenderStyle],
  );

  const isAutoGenerated = useCallback(
    (record: CMSDataRecord) =>
      record.name === PANORAMAS_COLLECTION_NAME ||
      record.name === HOTSPOTS_COLLECTION_NAME ||
      record.name === LEGACY_PANORAMAS_COLLECTION_NAME ||
      record.name === LEGACY_HOTSPOTS_COLLECTION_NAME,
    [],
  );
  const activeFields = useMemo(
    () => activeRecord?.data?.fields ?? activeRecord?.fieldStructure ?? [],
    [activeRecord],
  );
  const displayedFields = useMemo(() => {
    if (!activeRecord) return [];
    if (!isAutoGenerated(activeRecord) || showAllFields) {
      return activeFields;
    }
    if (
      activeRecord.name === HOTSPOTS_COLLECTION_NAME ||
      activeRecord.name === LEGACY_HOTSPOTS_COLLECTION_NAME
    ) {
      return activeFields.filter(
        (field: FieldStructure) =>
          field.name === "panorama_file" ||
          field.name === "name" ||
          field.name === "hotspot_style" ||
          isCustomField(field, activeRecord.name),
      );
    }
    if (
      activeRecord.name === PANORAMAS_COLLECTION_NAME ||
      activeRecord.name === LEGACY_PANORAMAS_COLLECTION_NAME
    ) {
      return activeFields.filter(
        (field: FieldStructure) =>
          field.name === "panorama_file" || field.name === "name" || isCustomField(field, activeRecord.name),
      );
    }
    return activeFields;
  }, [activeFields, activeRecord, isAutoGenerated, isCustomField, showAllFields]);
  const activeRows = useMemo(
    () => (activeRecord?.data?.rows ?? []) as Array<Record<string, unknown>>,
    [activeRecord],
  );
  const isHotspotsTable =
    activeRecord?.name === HOTSPOTS_COLLECTION_NAME ||
    activeRecord?.name === LEGACY_HOTSPOTS_COLLECTION_NAME;
  const isPanoramasTable =
    activeRecord?.name === PANORAMAS_COLLECTION_NAME ||
    activeRecord?.name === LEGACY_PANORAMAS_COLLECTION_NAME;
  const showAddFieldColumn = Boolean(
    activeRecord && isAutoGenerated(activeRecord) && (isHotspotsTable || isPanoramasTable),
  );
  const isDraftRow = useCallback(
    (row: Record<string, unknown>) => Boolean((row as { __draft?: boolean }).__draft),
    [],
  );
  const updateActiveRow = useCallback(
    (rowIndex: number, updates: Record<string, unknown>) => {
      setActiveRecord((prev) => {
        if (!prev) return prev;
        const currentData = prev.data as
          | {
              fields?: FieldStructure[];
              rows?: Array<Record<string, unknown>>;
              metadata?: Record<string, unknown>;
            }
          | undefined;

        const fields =
          (currentData?.fields as FieldStructure[] | undefined) ||
          (prev.fieldStructure as FieldStructure[] | undefined) ||
          [];
        const rows = currentData?.rows ?? [];
        if (!rows[rowIndex]) return prev;

        const nextRows = [...rows];
        nextRows[rowIndex] = { ...nextRows[rowIndex], ...updates };

        const nextData = {
          ...(currentData ?? { fields, rows: [] }),
          fields,
          rows: nextRows,
          metadata: {
            ...(currentData?.metadata ?? {}),
            rowCount: nextRows.length,
          },
        };

        return {
          ...prev,
          rowCount: nextRows.length,
          data: nextData,
        };
      });
    },
    [],
  );

  const isHotspotsActive = useMemo(
    () =>
      activeRecord?.name === HOTSPOTS_COLLECTION_NAME ||
      activeRecord?.name === LEGACY_HOTSPOTS_COLLECTION_NAME,
    [activeRecord],
  );
  const getFieldLabel = useCallback((field: FieldStructure, recordName?: string | null) => {
    const label = field.displayName || field.name;
    const isHotspotsRecord =
      recordName === HOTSPOTS_COLLECTION_NAME ||
      recordName === LEGACY_HOTSPOTS_COLLECTION_NAME;
    if (field.name === "panorama_file" && isHotspotsRecord) {
      return "Linked Panorama";
    }
    if (
      field.name === "panorama_file" &&
      (recordName === PANORAMAS_COLLECTION_NAME ||
        recordName === LEGACY_PANORAMAS_COLLECTION_NAME)
    ) {
      return "Panorama File";
    }
    if (field.name === "name" && isHotspotsRecord) {
      return "Hotspot Name";
    }
    if (
      field.name === "name" &&
      (recordName === PANORAMAS_COLLECTION_NAME ||
        recordName === LEGACY_PANORAMAS_COLLECTION_NAME)
    ) {
      return "Panorama Name";
    }
    if (field.name !== "id") return label;
    if (
      recordName === HOTSPOTS_COLLECTION_NAME ||
      recordName === LEGACY_HOTSPOTS_COLLECTION_NAME
    ) {
      return "Hotspot ID";
    }
    if (
      recordName === PANORAMAS_COLLECTION_NAME ||
      recordName === LEGACY_PANORAMAS_COLLECTION_NAME
    ) {
      return "Panorama ID";
    }
    return label;
  }, []);

  const draftStorageKey = useMemo(
    () => (projectId ? `cms_draft_hotspots_${projectId}` : null),
    [projectId],
  );
  const loadDraftHotspots = useCallback((): Array<Record<string, unknown>> => {
    if (!draftStorageKey) return [];
    try {
      const raw = localStorage.getItem(draftStorageKey);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn("Failed to load draft hotspots:", error);
      return [];
    }
  }, [draftStorageKey]);
  const saveDraftHotspots = useCallback(
    (drafts: Array<Record<string, unknown>>) => {
      if (!draftStorageKey) return;
      try {
        localStorage.setItem(draftStorageKey, JSON.stringify(drafts));
      } catch (error) {
        console.warn("Failed to save draft hotspots:", error);
      }
    },
    [draftStorageKey],
  );
  const appendDraftHotspotRow = useCallback(
    (draftRow: Record<string, unknown>) => {
      const drafts = loadDraftHotspots();
      saveDraftHotspots([...drafts, draftRow]);
    },
    [loadDraftHotspots, saveDraftHotspots],
  );
  const appendDraftHotspotRows = useCallback(
    (draftRows: Array<Record<string, unknown>>) => {
      if (draftRows.length === 0) return;
      const drafts = loadDraftHotspots();
      saveDraftHotspots([...drafts, ...draftRows]);
    },
    [loadDraftHotspots, saveDraftHotspots],
  );
  const removeDraftHotspotRow = useCallback(
    (draftId: string) => {
      const drafts = loadDraftHotspots();
      if (drafts.length === 0) return;
      if (!draftId) {
        const nextDrafts = drafts.filter(
          (row) => String((row as { id?: unknown }).id ?? "").trim() !== "",
        );
        saveDraftHotspots(nextDrafts);
        return;
      }
      const nextDrafts = drafts.filter(
        (row) => String((row as { id?: unknown }).id ?? "") !== draftId,
      );
      saveDraftHotspots(nextDrafts);
    },
    [loadDraftHotspots, saveDraftHotspots],
  );
  const updateDraftHotspotRow = useCallback(
    (draftId: string, updates: Record<string, unknown>) => {
      if (!draftId) return;
      const drafts = loadDraftHotspots();
      if (drafts.length === 0) return;
      const nextDrafts = drafts.map((row) =>
        String((row as { id?: unknown }).id ?? "") === draftId ? { ...row, ...updates } : row,
      );
      saveDraftHotspots(nextDrafts);
    },
    [loadDraftHotspots, saveDraftHotspots],
  );
  const mergeDraftHotspotsIntoRecord = useCallback(
    (record: CMSDataRecord) => {
      if (
        record.name !== HOTSPOTS_COLLECTION_NAME &&
        record.name !== LEGACY_HOTSPOTS_COLLECTION_NAME
      ) {
        return record;
      }

      const drafts = loadDraftHotspots().filter(
        (row) => String((row as { id?: unknown }).id ?? "").trim() !== "",
      );
      if (drafts.length === 0) return record;

      const currentData = record.data as
        | {
            fields?: FieldStructure[];
            rows?: Array<Record<string, unknown>>;
            metadata?: Record<string, unknown>;
          }
        | undefined;
      const rows = currentData?.rows ?? [];
      const existingIds = new Set(rows.map((row) => String((row as { id?: unknown }).id ?? "")));
      const mergedDrafts = drafts.filter(
        (row) => !existingIds.has(String((row as { id?: unknown }).id ?? "")),
      );
      if (mergedDrafts.length === 0) return record;

      const nextRows = [...rows, ...mergedDrafts];
      return {
        ...record,
        rowCount: nextRows.length,
        data: {
          ...(currentData ?? {}),
          rows: nextRows,
          metadata: {
            ...(currentData?.metadata ?? {}),
            rowCount: nextRows.length,
          },
        },
      };
    },
    [loadDraftHotspots],
  );

  const persistDatasetChanges = useCallback(
    async (nextFields: FieldStructure[], nextRows: Array<Record<string, unknown>>) => {
    if (!activeRecord || !project) return;
    try {
      setIsSavingCustomField(true);
      const craftspaceId = project.craftspace_id || "default";
      const cleanedRows = nextRows.filter(
        (row) => !Boolean((row as { __draft?: boolean }).__draft),
      );
      const updatedRecord = await CMSService.updateDatasetContent(
        activeRecord.id,
        craftspaceId,
        {
          fields: nextFields,
          rows: cleanedRows,
            metadata: {
              ...(activeRecord.data?.metadata ?? {}),
              rowCount: cleanedRows.length,
              fieldCount: nextFields.length,
            },
          },
        );
        const mergedRecord = mergeDraftHotspotsIntoRecord(updatedRecord);
        setActiveRecord(mergedRecord);
        setCmsRecords((prev) =>
          prev.map((record) =>
            record.id === mergedRecord.id
              ? { ...record, fieldCount: nextFields.length, rowCount: cleanedRows.length }
              : record,
          ),
        );
        if (selectedRecord?.id === mergedRecord.id) {
          setSelectedRecord(mergedRecord);
        }
      } catch (error) {
        console.error("Failed to save custom field changes:", error);
        toast({
          variant: "destructive",
          title: "Failed to save changes",
          description: error instanceof Error ? error.message : "Could not save custom fields.",
        });
      } finally {
        setIsSavingCustomField(false);
      }
    },
    [activeRecord, mergeDraftHotspotsIntoRecord, project?.craftspace_id, selectedRecord?.id, toast],
  );

  const pushCustomFieldUndo = useCallback((action: CustomFieldUndoAction) => {
    const stack = customFieldUndoStackRef.current;
    const next = [...stack, action].slice(-50);
    customFieldUndoStackRef.current = next;
  }, []);

  const handleDeleteCustomField = useCallback(
    async (field: FieldStructure) => {
      if (!activeRecord) return;
      if (!isCustomField(field, activeRecord.name)) return;

      const fields =
        (activeRecord.data?.fields as FieldStructure[] | undefined) ||
        (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
        [];
      const index = fields.findIndex((item) => item.name === field.name);
      if (index === -1) return;

      const rows = (activeRecord.data?.rows ?? []) as Array<Record<string, unknown>>;
      const values = rows.map((row) => row[field.name]);
      const nextFields = fields.filter((item) => item.name !== field.name);
      const nextRows = rows.map((row) => {
        const { [field.name]: _removed, ...rest } = row as Record<string, unknown>;
        return rest;
      });

      const nextData = {
        ...(activeRecord.data ?? { fields: nextFields, rows: [] }),
        fields: nextFields,
        rows: nextRows,
        metadata: {
          ...(activeRecord.data?.metadata ?? {}),
          fieldCount: nextFields.length,
          rowCount: nextRows.length,
        },
      };

      const nextRecord: CMSDataRecord = {
        ...activeRecord,
        fieldStructure: nextFields,
        fieldCount: nextFields.length,
        data: nextData,
      };

      pushCustomFieldUndo({
        type: "delete",
        field,
        index,
        values,
        width: columnWidths[field.name],
      });

      setActiveRecord(nextRecord);
      setCmsRecords((prev) =>
        prev.map((record) =>
          record.id === nextRecord.id
            ? { ...record, fieldCount: nextFields.length, rowCount: nextRows.length }
            : record,
        ),
      );
      if (selectedCell?.fieldName === field.name) {
        setSelectedCell(null);
        setEditingCell(null);
      }
      setColumnWidths((prev) => {
        if (!(field.name in prev)) return prev;
        const { [field.name]: _removed, ...rest } = prev;
        return rest;
      });

      await persistDatasetChanges(nextFields, nextRows);
    },
    [
      activeRecord,
      columnWidths,
      isCustomField,
      persistDatasetChanges,
      pushCustomFieldUndo,
      selectedCell?.fieldName,
    ],
  );

  const handleEditFieldOpen = useCallback(
    (field: FieldStructure) => {
      setEditingField(field);
      setEditFieldDateOptions(field.dateOptions ? { ...field.dateOptions } : { ...DEFAULT_DATE_OPTIONS });
      setEditFieldOptions(field.options ? [...field.options] : []);
      setEditFieldOptionInput("");
      setEditFieldDefaultValue(field.defaultValue ?? "");
      setEditFieldDialogOpen(true);
    },
    [],
  );

  const handleEditFieldSave = useCallback(async () => {
    if (!activeRecord || !editingField) return;

    const fields =
      (activeRecord.data?.fields as FieldStructure[] | undefined) ||
      (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
      [];
    const index = fields.findIndex((f) => f.name === editingField.name);
    if (index === -1) return;

    const updatedField: FieldStructure = {
      ...fields[index],
      ...(editingField.fieldType === "date" || editingField.fieldType === "datetime"
        ? { dateOptions: { ...editFieldDateOptions } }
        : {}),
      ...(editingField.fieldType === "singleselect"
        ? { options: [...editFieldOptions], defaultValue: editFieldDefaultValue || undefined }
        : {}),
    };

    const nextFields = [...fields];
    nextFields[index] = updatedField;

    const rows = (activeRecord.data?.rows ?? []) as Array<Record<string, unknown>>;
    const nextData = {
      ...(activeRecord.data ?? { fields: nextFields, rows }),
      fields: nextFields,
      rows,
      metadata: {
        ...(activeRecord.data?.metadata ?? {}),
        fieldCount: nextFields.length,
        rowCount: rows.length,
      },
    };

    const nextRecord: CMSDataRecord = {
      ...activeRecord,
      fieldStructure: nextFields,
      data: nextData,
    };

    setActiveRecord(nextRecord);
    setCmsRecords((prev) =>
      prev.map((record) =>
        record.id === nextRecord.id
          ? { ...record, fieldCount: nextFields.length, rowCount: rows.length }
          : record,
      ),
    );

    await persistDatasetChanges(nextFields, rows);
    setEditFieldDialogOpen(false);
    setEditingField(null);
  }, [activeRecord, editingField, editFieldDateOptions, editFieldOptions, editFieldDefaultValue, persistDatasetChanges]);

  const handleAddCustomField = useCallback(async () => {
    if (!activeRecord) return;
    if (!project) return;
    if (!isHotspotsTable && !isPanoramasTable) return;

    const rawName = newFieldName.trim();
    if (!rawName) {
      toast({
        variant: "destructive",
        title: "Field name required",
        description: "Please enter a field name.",
      });
      return;
    }

    const fieldName = sanitizeFieldName(rawName);
    if (!fieldName) {
      toast({
        variant: "destructive",
        title: "Invalid field name",
        description: "Please use letters, numbers, or underscores.",
      });
      return;
    }

    const fields =
      (activeRecord.data?.fields as FieldStructure[] | undefined) ||
      (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
      [];
    if (fields.some((field) => field.name === fieldName)) {
      toast({
        variant: "destructive",
        title: "Field already exists",
        description: `A field named "${fieldName}" already exists.`,
      });
      return;
    }

    const displayName = newFieldDisplayName.trim() || toDisplayName(fieldName);
    const nextField: FieldStructure = {
      name: fieldName,
      displayName,
      fieldType: newFieldType,
      ...(newFieldType === "date" ? { dateOptions: { ...newDateOptions } } : {}),
      ...(newFieldType === "singleselect" ? { options: [...newFieldOptions] } : {}),
    };

    const nextFields = [...fields, nextField];
    const rows = (activeRecord.data?.rows ?? []) as Array<Record<string, unknown>>;
    const nextRows = rows.map((row) => ({ ...row, [fieldName]: null }));

    const nextData = {
      ...(activeRecord.data ?? { fields: nextFields, rows: [] }),
      fields: nextFields,
      rows: nextRows,
      metadata: {
        ...(activeRecord.data?.metadata ?? {}),
        fieldCount: nextFields.length,
        rowCount: nextRows.length,
      },
    };

    const nextRecord: CMSDataRecord = {
      ...activeRecord,
      fieldStructure: nextFields,
      fieldCount: nextFields.length,
      data: nextData,
    };

    pushCustomFieldUndo({
      type: "add",
      field: nextField,
      index: nextFields.length - 1,
    });

    setActiveRecord(nextRecord);
    setCmsRecords((prev) =>
      prev.map((record) =>
        record.id === nextRecord.id
          ? { ...record, fieldCount: nextFields.length, rowCount: nextRows.length }
          : record,
      ),
    );

    await persistDatasetChanges(nextFields, nextRows);

    setNewFieldName("");
    setNewFieldDisplayName("");
    setNewFieldType("longtext");
    setNewDateOptions({ ...DEFAULT_DATE_OPTIONS });
    setNewFieldOptions([]);
    setNewFieldOptionInput("");
    setAddFieldDialogOpen(false);
  }, [
    activeRecord,
    isHotspotsTable,
    isPanoramasTable,
    newFieldDisplayName,
    newFieldName,
    newFieldType,
    newDateOptions,
    newFieldOptions,
    persistDatasetChanges,
    project?.craftspace_id,
    sanitizeFieldName,
    toast,
    toDisplayName,
    pushCustomFieldUndo,
  ]);

  const handleUndoCustomFieldChange = useCallback(async () => {
    if (!activeRecord) return;
    const stack = customFieldUndoStackRef.current;
    if (stack.length === 0) return;
    const action = stack[stack.length - 1];
    customFieldUndoStackRef.current = stack.slice(0, -1);

    const fields =
      (activeRecord.data?.fields as FieldStructure[] | undefined) ||
      (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
      [];
    const rows = (activeRecord.data?.rows ?? []) as Array<Record<string, unknown>>;

    if (action.type === "add") {
      if (!fields.some((field) => field.name === action.field.name)) return;
      const nextFields = fields.filter((field) => field.name !== action.field.name);
      const nextRows = rows.map((row) => {
        const { [action.field.name]: _removed, ...rest } = row as Record<string, unknown>;
        return rest;
      });

      const nextData = {
        ...(activeRecord.data ?? { fields: nextFields, rows: [] }),
        fields: nextFields,
        rows: nextRows,
        metadata: {
          ...(activeRecord.data?.metadata ?? {}),
          fieldCount: nextFields.length,
          rowCount: nextRows.length,
        },
      };

      const nextRecord: CMSDataRecord = {
        ...activeRecord,
        fieldStructure: nextFields,
        fieldCount: nextFields.length,
        data: nextData,
      };

      setActiveRecord(nextRecord);
      setCmsRecords((prev) =>
        prev.map((record) =>
          record.id === nextRecord.id
            ? { ...record, fieldCount: nextFields.length, rowCount: nextRows.length }
            : record,
        ),
      );
      if (selectedCell?.fieldName === action.field.name) {
        setSelectedCell(null);
        setEditingCell(null);
      }
      setColumnWidths((prev) => {
        if (!(action.field.name in prev)) return prev;
        const { [action.field.name]: _removed, ...rest } = prev;
        return rest;
      });

      await persistDatasetChanges(nextFields, nextRows);
      return;
    }

    if (fields.some((field) => field.name === action.field.name)) return;
    const insertIndex = Math.min(action.index, fields.length);
    const nextFields = [...fields];
    nextFields.splice(insertIndex, 0, action.field);
    const nextRows = rows.map((row, rowIndex) => ({
      ...row,
      [action.field.name]: action.values[rowIndex] ?? null,
    }));

    const nextData = {
      ...(activeRecord.data ?? { fields: nextFields, rows: [] }),
      fields: nextFields,
      rows: nextRows,
      metadata: {
        ...(activeRecord.data?.metadata ?? {}),
        fieldCount: nextFields.length,
        rowCount: nextRows.length,
      },
    };

    const nextRecord: CMSDataRecord = {
      ...activeRecord,
      fieldStructure: nextFields,
      fieldCount: nextFields.length,
      data: nextData,
    };

    setActiveRecord(nextRecord);
    setCmsRecords((prev) =>
      prev.map((record) =>
        record.id === nextRecord.id
          ? { ...record, fieldCount: nextFields.length, rowCount: nextRows.length }
          : record,
      ),
    );
    if (action.width !== undefined) {
      setColumnWidths((prev) => ({
        ...prev,
        [action.field.name]: action.width ?? prev[action.field.name],
      }));
    }

    await persistDatasetChanges(nextFields, nextRows);
  }, [activeRecord, persistDatasetChanges, selectedCell?.fieldName]);

  const commitCustomFieldEdit = useCallback(
    async (rowIndex: number, field: FieldStructure, value: string) => {
      if (!activeRecord) return;
      const fields =
        (activeRecord.data?.fields as FieldStructure[] | undefined) ||
        (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
        [];
      const rows = (activeRecord.data?.rows ?? []) as Array<Record<string, unknown>>;
      if (!rows[rowIndex]) return;

      const nextRows = [...rows];
      nextRows[rowIndex] = {
        ...nextRows[rowIndex],
        [field.name]: coerceFieldValue(value, field.fieldType),
      };

      const nextData = {
        ...(activeRecord.data ?? { fields, rows: [] }),
        fields,
        rows: nextRows,
        metadata: {
          ...(activeRecord.data?.metadata ?? {}),
          rowCount: nextRows.length,
          fieldCount: fields.length,
        },
      };

      const nextRecord: CMSDataRecord = {
        ...activeRecord,
        data: nextData,
      };
      setActiveRecord(nextRecord);
      await persistDatasetChanges(fields, nextRows);
    },
    [activeRecord, coerceFieldValue, persistDatasetChanges],
  );

  /** Paste multiple lines into consecutive rows of a custom field, starting at startRowIndex. */
  const commitMultiLinePaste = useCallback(
    async (startRowIndex: number, field: FieldStructure, lines: string[]) => {
      if (!activeRecord || lines.length === 0) return;
      const fields =
        (activeRecord.data?.fields as FieldStructure[] | undefined) ||
        (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
        [];
      const rows = (activeRecord.data?.rows ?? []) as Array<Record<string, unknown>>;

      const nextRows = [...rows];
      for (let i = 0; i < lines.length; i++) {
        const rowIdx = startRowIndex + i;
        if (rowIdx >= nextRows.length) break;
        nextRows[rowIdx] = {
          ...nextRows[rowIdx],
          [field.name]: coerceFieldValue(lines[i], field.fieldType),
        };
      }

      const nextData = {
        ...(activeRecord.data ?? { fields, rows: [] }),
        fields,
        rows: nextRows,
        metadata: {
          ...(activeRecord.data?.metadata ?? {}),
          rowCount: nextRows.length,
          fieldCount: fields.length,
        },
      };

      const nextRecord: CMSDataRecord = { ...activeRecord, data: nextData };
      setActiveRecord(nextRecord);
      setEditingCell(null);
      await persistDatasetChanges(fields, nextRows);
    },
    [activeRecord, coerceFieldValue, persistDatasetChanges],
  );

  const handleLinkDraftPanorama = useCallback(
    async (rowIndex: number, panorama: Panorama) => {
      const thumbnail = panorama.thumbnail_url_webp || panorama.thumbnail_url || "";
      const row = activeRows[rowIndex];
      const draftId = String(row?.id ?? "");
      const isDraft = Boolean((row as { __draft?: boolean })?.__draft);
      const rowName = typeof row?.name === "string" ? row.name : "";

      if (isDraft) {
        try {
          if (!project) return;
          if (!user && !isLocalBackend) {
            throw new Error("Please sign in to link a panorama.");
          }

          let createdHotspot: Hotspot;
          if (isLocalBackend) {
            const hotspotId = await createLocalHotspot({
              project_id: project.id,
              craftspace_id: project.craftspace_id || "default",
              panorama_id: panorama.id,
              name: rowName,
              type: "html",
              content: defaultHtmlHotspotContent,
              visible: false,
              order: hotspots?.length ?? 0,
            });
            createdHotspot = {
              id: hotspotId,
              project_id: project.id,
              panorama_id: panorama.id,
              name: rowName,
              type: "html",
              content: defaultHtmlHotspotContent,
              visible: false,
              order: hotspots?.length ?? 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            } as Hotspot;
          } else {
            createdHotspot = (await convex.mutation(api.hotspots.create, {
              project_id: project.id,
              panorama_id: panorama.id,
              name: rowName,
              type: "html",
              content: defaultHtmlHotspotContent,
              visible: false,
              order: hotspots?.length ?? 0,
            })) as Hotspot;
          }

          addHotspot(createdHotspot);
          updateActiveRow(rowIndex, {
            id: createdHotspot.id,
            name: createdHotspot.name ?? rowName,
            type: createdHotspot.type ?? "html",
            panorama_id: panorama.id,
            panorama_name: panorama.name,
            panorama_file: thumbnail,
            __draft: false,
          });
          removeDraftHotspotRow(draftId);
          return;
        } catch (error) {
          console.error("Failed to link panorama:", error);
          toast({
            variant: "destructive",
            title: "Link failed",
            description: error instanceof Error ? error.message : "Failed to link panorama.",
          });
          return;
        }
      }

      const hotspotId = String(row?.id ?? "");
      try {
        if (hotspotId) {
          if (!user && !isLocalBackend) {
            throw new Error("Please sign in to link a panorama.");
          }
          if (isLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, panorama_id: panorama.id });
          } else {
            await convex.mutation(api.hotspots.update, { id: hotspotId, panorama_id: panorama.id });
          }
          updateHotspot(hotspotId, { panorama_id: panorama.id });
        }
      } catch (error) {
        console.error("Failed to link panorama:", error);
        toast({
          variant: "destructive",
          title: "Link failed",
          description: error instanceof Error ? error.message : "Failed to link panorama.",
        });
        return;
      }

      updateActiveRow(rowIndex, {
        panorama_id: panorama.id,
        panorama_name: panorama.name,
        panorama_file: thumbnail,
      });
    },
    [
      activeRows,
      addHotspot,
      convex,
      isLocalBackend,
      project,
      removeDraftHotspotRow,
      toast,
      updateActiveRow,
      user,
      hotspots,
      updateHotspot,
    ],
  );

  const insertDraftHotspotRow = useCallback(
    (afterIndex: number) => {
      if (!activeRecord || !isHotspotsActive) return;

      const currentData = activeRecord.data as
        | {
            fields?: FieldStructure[];
            rows?: Array<Record<string, unknown>>;
            metadata?: Record<string, unknown>;
          }
        | undefined;

      const fields =
        (currentData?.fields as FieldStructure[] | undefined) ||
        (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
        [];
      if (!fields.some((field) => field.name === "name")) return;

      const rows = currentData?.rows ?? [];
      const draftRow: Record<string, unknown> = {};

      for (const field of fields) {
        if (
          (field.fieldType === "date" || field.fieldType === "datetime") &&
          field.dateOptions?.defaultToCurrentDate
        ) {
          draftRow[field.name] = new Date().toISOString().slice(0, field.dateOptions?.includeTime ? 19 : 10);
        } else {
          draftRow[field.name] = null;
        }
      }

      draftRow.name = "";
      draftRow.panorama_id = "";
      draftRow.panorama_name = "";
      draftRow.panorama_file = "";
      draftRow.__draft = true;
      if (draftRow.id === null || draftRow.id === undefined) {
        draftRow.id = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      }

      const insertIndex = Math.min(Math.max(afterIndex + 1, 0), rows.length);
      const nextRows = [...rows];
      nextRows.splice(insertIndex, 0, draftRow);

      const nextData = {
        ...(currentData ?? { fields, rows: [] }),
        fields,
        rows: nextRows,
        metadata: {
          ...(currentData?.metadata ?? {}),
          rowCount: nextRows.length,
        },
      };

      setActiveRecord({
        ...activeRecord,
        rowCount: nextRows.length,
        data: nextData,
      });

      appendDraftHotspotRow(draftRow);
      setSelectedCell({ rowIndex: insertIndex, fieldName: "name" });
      setEditingCell({ rowIndex: insertIndex, fieldName: "name", value: "" });
    },
    [activeRecord, isHotspotsActive, appendDraftHotspotRow],
  );
  const insertDraftHotspotRows = useCallback(
    (afterIndex: number, names: string[]) => {
      if (!activeRecord || !isHotspotsActive) return;
      if (names.length === 0) return;

      const currentData = activeRecord.data as
        | {
            fields?: FieldStructure[];
            rows?: Array<Record<string, unknown>>;
            metadata?: Record<string, unknown>;
          }
        | undefined;

      const fields =
        (currentData?.fields as FieldStructure[] | undefined) ||
        (activeRecord.fieldStructure as FieldStructure[] | undefined) ||
        [];
      if (!fields.some((field) => field.name === "name")) return;

      const rows = currentData?.rows ?? [];
      const makeDraftRow = (name: string) => {
        const draftRow: Record<string, unknown> = {};
        for (const field of fields) {
          if (
            (field.fieldType === "date" || field.fieldType === "datetime") &&
            field.dateOptions?.defaultToCurrentDate
          ) {
            draftRow[field.name] = new Date().toISOString().slice(0, field.dateOptions?.includeTime ? 19 : 10);
          } else {
            draftRow[field.name] = null;
          }
        }
        draftRow.name = name;
        draftRow.panorama_id = "";
        draftRow.panorama_name = "";
        draftRow.panorama_file = "";
        draftRow.__draft = true;
        if (draftRow.id === null || draftRow.id === undefined) {
          draftRow.id = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        }
        return draftRow;
      };

      const newRows = names.map(makeDraftRow);
      const insertIndex = Math.min(Math.max(afterIndex + 1, 0), rows.length);
      const nextRows = [...rows];
      nextRows.splice(insertIndex, 0, ...newRows);

      const nextData = {
        ...(currentData ?? { fields, rows: [] }),
        fields,
        rows: nextRows,
        metadata: {
          ...(currentData?.metadata ?? {}),
          rowCount: nextRows.length,
        },
      };

      setActiveRecord({
        ...activeRecord,
        rowCount: nextRows.length,
        data: nextData,
      });

      appendDraftHotspotRows(newRows);
    },
    [activeRecord, isHotspotsActive, appendDraftHotspotRows],
  );

  const loadCMSData = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!projectId) return;
      const silent = options?.silent ?? false;

      try {
        if (!silent) {
          setIsLoading(true);
        }
        const records = await CMSService.loadProjectCMSData(projectId);
        setCmsRecords(records);
      } catch (error) {
        console.error("Error loading CMS data:", error);

        // Show different message if table doesn't exist
        const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
        const isTableMissing = errorMessage.includes("does not exist");

        if (!isTableMissing && !silent) {
          toast({
            variant: "destructive",
            title: "Failed to load CMS data",
            description: errorMessage,
          });
        }
      } finally {
        if (!silent) {
          setIsLoading(false);
        }
      }
    },
    [projectId, toast],
  );

  // Load existing CMS data on component mount
  // biome-ignore lint/correctness/useExhaustiveDependencies: loadCMSData changes on every render but causes infinite loop if included
  useEffect(() => {
    const initializeCMS = async () => {
      if (!projectId || !project) return;

      try {
        // First clean up any existing duplicates
        const craftspaceId = project.craftspace_id || "default";
        await CMSService.cleanupDuplicateCollections(projectId, craftspaceId);

        // Then load the data
        await loadCMSData({ silent: true });
      } catch (error) {
        console.error("Error initializing CMS:", error);
        // Still try to load data even if cleanup fails
        await loadCMSData({ silent: true });
      }
    };

    initializeCMS();
  }, [projectId, project]);

  useEffect(() => {
    if (cmsRecords.length === 0) {
      setActiveRecordId(null);
      setActiveRecord(null);
      return;
    }

    if (!activeRecordId || !cmsRecords.some((record) => record.id === activeRecordId)) {
      setActiveRecordId(cmsRecords[0].id);
    }
  }, [cmsRecords, activeRecordId]);

  useEffect(() => {
    setHasLoadedOnce(false);
  }, [projectId]);

  useEffect(() => {
    if (!activeRecordId || !project?.craftspace_id) return;

    let cancelled = false;
    const loadActiveRecord = async () => {
      try {
        setIsActiveLoading(true);
        const recordWithData = await CMSService.loadCMSDataWithContent(
          activeRecordId,
          project.craftspace_id,
        );
        if (!cancelled) {
          const mergedRecord = mergeDraftHotspotsIntoRecord(recordWithData);
          setActiveRecord(mergedRecord);
          setHasLoadedOnce(true);
        }
      } catch (error) {
        console.error("Error loading active CMS data:", error);
        if (!cancelled) {
          toast({
            variant: "destructive",
            title: "Failed to load CMS data",
            description: error instanceof Error ? error.message : "Unknown error occurred",
          });
        }
      } finally {
        if (!cancelled) {
          setIsActiveLoading(false);
        }
      }
    };

    loadActiveRecord();
    return () => {
      cancelled = true;
    };
  }, [activeRecordId, project?.craftspace_id, toast, mergeDraftHotspotsIntoRecord, activeRecordRefreshKey]);

  useEffect(() => {
    setEditingCell(null);
    setSelectedCell(null);
    setSelectedRowKeys(new Set());
    lastSelectedRowIndexRef.current = null;
    customFieldUndoStackRef.current = [];
  }, [activeRecordId]);

  useEffect(() => {
    if (!columnWidthsStorageKey) {
      setColumnWidths({});
      return;
    }
    try {
      const raw = localStorage.getItem(columnWidthsStorageKey);
      if (!raw) {
        setColumnWidths({});
        return;
      }
      const parsed = JSON.parse(raw);
      setColumnWidths(typeof parsed === "object" && parsed ? parsed : {});
    } catch (error) {
      console.warn("Failed to load column widths:", error);
      setColumnWidths({});
    }
  }, [columnWidthsStorageKey]);

  useEffect(() => {
    if (!columnWidthsStorageKey) return;
    try {
      localStorage.setItem(columnWidthsStorageKey, JSON.stringify(columnWidths));
    } catch (error) {
      console.warn("Failed to save column widths:", error);
    }
  }, [columnWidths, columnWidthsStorageKey]);

  useEffect(() => {
    const isEditableTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      const tagName = target.tagName.toLowerCase();
      return (
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select" ||
        target.isContentEditable
      );
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const isUndo =
        (event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === "z";
      if (!isUndo) return;
      if (editingCell) return;
      if (isEditableTarget(event.target)) return;
      if (customFieldUndoStackRef.current.length === 0) return;
      event.preventDefault();
      void handleUndoCustomFieldChange();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [editingCell, handleUndoCustomFieldChange]);

  useEffect(() => {
    if (!activeRecordId || !activeRecord?.data?.rows || !project?.craftspace_id) return;
    const rowCount = activeRecord.rowCount ?? 0;
    if (
      rowCount > 0 &&
      activeRecord.data.rows.length < rowCount &&
      !attemptedCMSResyncRef.current
    ) {
      attemptedCMSResyncRef.current = true;
      let cancelled = false;
      (async () => {
        try {
          const recordWithData = await CMSService.loadCMSDataWithContent(
            activeRecordId,
            project.craftspace_id,
          );
          if (!cancelled) {
            const mergedRecord = mergeDraftHotspotsIntoRecord(recordWithData);
            setActiveRecord(mergedRecord);
          }
        } catch (error) {
          console.error("Failed to resync CMS data:", error);
        }
      })();
      return () => {
        cancelled = true;
      };
    }
    if (activeRecord.data.rows.length >= rowCount) {
      attemptedCMSResyncRef.current = false;
    }
  }, [
    activeRecord?.data?.rows,
    activeRecord?.rowCount,
    activeRecordId,
    mergeDraftHotspotsIntoRecord,
    project?.craftspace_id,
  ]);

  useEffect(() => {
    if (!activeRecord || !isHotspotsTable || !hotspots) return;
    if (!activeRecord.data?.rows) return;
    if (suppressHotspotRefreshRef.current) return;

    const hotspotIds = new Set(hotspots.map((hotspot) => hotspot.id));
    const rows = activeRecord.data.rows as Array<Record<string, unknown>>;
    const nonDraftRowCount = rows.filter(
      (row) => !Boolean((row as { __draft?: boolean }).__draft),
    ).length;
    if (hotspots.length < nonDraftRowCount) return;
    const filteredRows = rows.filter((row) => {
      const rowId = String((row as { id?: unknown }).id ?? "");
      const isDraft = Boolean((row as { __draft?: boolean }).__draft);
      return isDraft || hotspotIds.has(rowId);
    });

    if (filteredRows.length === rows.length) return;
    const removedRows = rows
      .filter((row) => {
        const rowId = String((row as { id?: unknown }).id ?? "");
        const isDraft = Boolean((row as { __draft?: boolean }).__draft);
        return !isDraft && !hotspotIds.has(rowId);
      })
      .map((row) => ({
        id: String((row as { id?: unknown }).id ?? ""),
        name: typeof row.name === "string" ? row.name : "",
      }));
    console.warn("[CMS] Filtered hotspot rows missing from store:", removedRows);

    setActiveRecord((prev) => {
      if (!prev?.data?.rows) return prev;
      return {
        ...prev,
        rowCount: filteredRows.length,
        data: {
          ...prev.data,
          rows: filteredRows,
          metadata: {
            ...(prev.data.metadata ?? {}),
            rowCount: filteredRows.length,
          },
        },
      };
    });
  }, [activeRecord, hotspots, isHotspotsTable]);

  useEffect(() => {
    const pendingPanoramaId = pendingPanoramaSelectRef.current;
    if (!pendingPanoramaId) return;
    if (!activeRecord || !isPanoramasTable || !activeRecord.data?.rows) return;

    const rows = activeRecord.data.rows as Array<Record<string, unknown>>;
    const rowIndex = rows.findIndex(
      (row) => String((row as { id?: unknown }).id ?? "") === pendingPanoramaId,
    );
    if (rowIndex >= 0) {
      setSelectedCell({ rowIndex, fieldName: "name" });
    }
    pendingPanoramaSelectRef.current = null;
  }, [activeRecord, isPanoramasTable]);

  if (!project) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-white">Project not found</div>
      </div>
    );
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !projectId || !project?.craftspace_id) return;
    if (!user && !isLocalBackend) return;

    const isCSV = file.name.toLowerCase().endsWith(".csv");
    const isJSON = file.name.toLowerCase().endsWith(".json");

    if (!isCSV && !isJSON) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please select a CSV or JSON file.",
      });
      return;
    }

    setIsUploading(true);
    try {
      const craftspaceId = project.craftspace_id;

      let record: CMSDataRecord;

      if (isCSV) {
        // Handle CSV import (existing functionality)
        record = await CMSService.importCsvFile(file, projectId, userId, craftspaceId, {
          name: file.name.replace(/\.(csv|json)$/i, ""),
          description: importDescription || undefined,
        });
      } else {
        // Handle JSON import (new functionality)
        record = await CMSService.importJsonFile(file, projectId, userId, craftspaceId, {
          name: file.name.replace(/\.(csv|json)$/i, ""),
          description: importDescription || undefined,
        });
      }

      // Add to local state
      setCmsRecords((prev) => [record, ...prev]);

      // Reset form
      setImportName("");
      setImportDescription("");

      toast({
        title: `${isCSV ? "CSV" : "JSON"} imported successfully`,
        description: `Imported ${record.rowCount} items with ${record.fieldCount} fields.`,
      });
    } catch (error) {
      console.error("Error importing file:", error);
      toast({
        variant: "destructive",
        title: "Import failed",
        description: error instanceof Error ? error.message : "Failed to import file.",
      });
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleViewData = async (record: CMSDataRecord) => {
    if (!project?.craftspace_id) return;

    try {
      setIsLoading(true);
      const recordWithData = await CMSService.loadCMSDataWithContent(
        record.id,
        project.craftspace_id,
      );
      setSelectedRecord(recordWithData);
      setViewDialogOpen(true);
    } catch (error) {
      console.error("Error loading CMS data content:", error);
      toast({
        variant: "destructive",
        title: "Failed to load data",
        description: error instanceof Error ? error.message : "Could not load JSON data",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportData = async (record: CMSDataRecord) => {
    if (!project?.craftspace_id) return;

    try {
      let recordWithData = record;

      // Load data if not already loaded
      if (!record.data) {
        recordWithData = await CMSService.loadCMSDataWithContent(record.id, project.craftspace_id);
      }

      await CMSService.exportAsCSV(recordWithData);

      toast({
        title: "Export successful",
        description: "CSV file has been downloaded.",
      });
    } catch (error) {
      console.error("Error exporting data:", error);
      toast({
        variant: "destructive",
        title: "Export failed",
        description: error instanceof Error ? error.message : "Failed to export data.",
      });
    }
  };

  const handleDeleteRecord = async (recordId: string) => {
    if (!project?.craftspace_id) return;

    try {
      await CMSService.deleteCMSData(recordId, project.craftspace_id);

      // Remove from local state
      setCmsRecords((prev) => prev.filter((r) => r.id !== recordId));

      toast({
        title: "Data deleted",
        description: "CMS data has been permanently deleted.",
      });
    } catch (error) {
      console.error("Error deleting CMS data:", error);
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: error instanceof Error ? error.message : "Failed to delete data.",
      });
    }
  };


  const handleRenameRecord = async (record: CMSDataRecord) => {
    const nextName = window.prompt("Rename table", record.name);
    if (!nextName) return;
    const trimmed = nextName.trim();
    if (!trimmed || trimmed === record.name) return;

    try {
      const updated = await CMSService.renameDataset(record.id, trimmed);
      setCmsRecords((prev) =>
        prev.map((r) => (r.id === record.id ? { ...r, name: updated.name } : r)),
      );
      if (activeRecordId === record.id) {
        setActiveRecord((prev) => (prev ? { ...prev, name: updated.name } : prev));
      }
      toast({
        title: "Table renamed",
        description: `Renamed to "${updated.name}".`,
      });
    } catch (error) {
      console.error("Error renaming CMS table:", error);
      toast({
        variant: "destructive",
        title: "Rename failed",
        description: error instanceof Error ? error.message : "Failed to rename table.",
      });
    }
  };

  const commitHotspotNameEditPanel = async (valueOverride?: string) => {
    if (!editingCell || !isHotspotsTable) return;
    if (editingCell.fieldName !== "name") return;
    const row = activeRows[editingCell.rowIndex];
    const hotspotId = String(row?.id ?? "");
    const isDraft = Boolean((row as { __draft?: boolean })?.__draft);

    const nextName = (valueOverride ?? editingCell.value).trim();
    const currentName = typeof row?.name === "string" ? row.name : "";
    if (!nextName || nextName === currentName) {
      setEditingCell(null);
      return;
    }

    if (isDraft || !hotspotId) {
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        rows[editingCell.rowIndex] = {
          ...rows[editingCell.rowIndex],
          name: nextName,
        } as Record<string, unknown>;
        return { ...prev, data: { ...prev.data, rows } };
      });
      if (isDraft && hotspotId) {
        updateDraftHotspotRow(hotspotId, { name: nextName });
      }
      setEditingCell(null);
      return;
    }

    try {
      suppressHotspotRefreshRef.current = true;
      // Optimistic UI update
      updateHotspot(hotspotId, { name: nextName });
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        rows[editingCell.rowIndex] = {
          ...rows[editingCell.rowIndex],
          name: nextName,
        } as Record<string, unknown>;
        return { ...prev, data: { ...prev.data, rows } };
      });

      if (!user && !isLocalBackend) {
        throw new Error("You must be signed in to edit hotspot names.");
      }

      if (isLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await updateLocalHotspot({ id: hotspotId, craftspace_id: craftspaceId, name: nextName });
      } else {
        await convex.mutation(api.hotspots.update, { id: hotspotId, name: nextName });
      }

      if (projectId && project) {
        const craftspaceId = project.craftspace_id || "default";
        await CMSService.refreshHotspotsDataset(projectId, userId, craftspaceId);
        await loadCMSData({ silent: true });
        setActiveRecordRefreshKey((k) => k + 1);
      }
    } catch (error) {
      // Revert on failure
      updateHotspot(hotspotId, { name: currentName });
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        rows[editingCell.rowIndex] = {
          ...rows[editingCell.rowIndex],
          name: currentName,
        } as Record<string, unknown>;
        return { ...prev, data: { ...prev.data, rows } };
      });
      console.error("Error updating hotspot name:", error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update hotspot name.",
      });
    } finally {
      suppressHotspotRefreshRef.current = false;
      setEditingCell(null);
    }
  };

  const commitHotspotStyleEditPanel = async (valueOverride?: string) => {
    if (!editingCell || !isHotspotsTable) return;
    if (editingCell.fieldName !== "hotspot_style") return;
    const row = activeRows[editingCell.rowIndex];
    const currentValue = typeof row?.hotspot_style === "string" ? row.hotspot_style : "";
    const nextValue = (valueOverride ?? editingCell.value).trim();
    if (nextValue === currentValue) {
      setEditingCell(null);
      return;
    }

    suppressHotspotRefreshRef.current = true;
    try {
      await applyHotspotStyleToRows([editingCell.rowIndex], nextValue);
    } finally {
      suppressHotspotRefreshRef.current = false;
      setEditingCell(null);
    }
  };

  const commitPanoramaNameEditPanel = async (valueOverride?: string) => {
    if (!editingCell || !isPanoramasTable) return;
    const row = activeRows[editingCell.rowIndex];
    const panoramaId = String(row?.id ?? "");
    if (!panoramaId) {
      setEditingCell(null);
      return;
    }

    const nextName = (valueOverride ?? editingCell.value).trim();
    const currentName = typeof row?.name === "string" ? row.name : "";
    if (!nextName || nextName === currentName) {
      setEditingCell(null);
      return;
    }

    try {
      // Optimistic UI update
      updatePanorama(panoramaId, { name: nextName });
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        rows[editingCell.rowIndex] = {
          ...rows[editingCell.rowIndex],
          name: nextName,
        } as Record<string, unknown>;
        return { ...prev, data: { ...prev.data, rows } };
      });

      if (!user && !isLocalBackend) {
        throw new Error("You must be signed in to edit panorama names.");
      }

      if (isLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await updateLocalPanorama({ id: panoramaId, craftspace_id: craftspaceId, name: nextName });
      } else {
        await convex.mutation(api.panoramas.update, { id: panoramaId, name: nextName });
      }
    } catch (error) {
      // Revert on failure
      updatePanorama(panoramaId, { name: currentName });
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        rows[editingCell.rowIndex] = {
          ...rows[editingCell.rowIndex],
          name: currentName,
        } as Record<string, unknown>;
        return { ...prev, data: { ...prev.data, rows } };
      });
      console.error("Error updating panorama name:", error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update panorama name.",
      });
    } finally {
      setEditingCell(null);
    }
  };

  const commitNameEditPanel = async (valueOverride?: string) => {
    if (isHotspotsTable && editingCell?.fieldName === "hotspot_style") {
      await commitHotspotStyleEditPanel(valueOverride);
      return;
    }
    if (isHotspotsTable) {
      await commitHotspotNameEditPanel(valueOverride);
      return;
    }
    if (isPanoramasTable) {
      await commitPanoramaNameEditPanel(valueOverride);
      return;
    }
    setEditingCell(null);
  };

  const handleOpenHotspotInEditor = useCallback(
    (row: Record<string, unknown>) => {
      if (!projectId) return;
      const hotspotId = String((row as { id?: unknown }).id ?? "");
      if (!hotspotId) return;
      const panoramaId = String((row as { panorama_id?: unknown }).panorama_id ?? "");
      const panorama = panoramas?.find((p) => p.id === panoramaId) ?? null;
      const hotspot = hotspots?.find((h) => h.id === hotspotId) ?? null;

      if (panorama) {
        setSelectedPanorama(panorama);
      }
      if (hotspot) {
        setSelectedHotspot(hotspot);
      }
      requestFocusHotspot(hotspotId);
      navigate(`/${projectId}/editor`);
    },
    [
      navigate,
      panoramas,
      projectId,
      requestFocusHotspot,
      setSelectedHotspot,
      setSelectedPanorama,
      hotspots,
    ],
  );

  const handleOpenPanoramaInCMS = useCallback(
    (panoramaId: string) => {
      const panoramasRecord = cmsRecords.find(
        (record) =>
          record.name === PANORAMAS_COLLECTION_NAME ||
          record.name === LEGACY_PANORAMAS_COLLECTION_NAME,
      );
      if (!panoramasRecord) return;
      pendingPanoramaSelectRef.current = panoramaId;
      setActiveRecordId(panoramasRecord.id);
    },
    [cmsRecords, setActiveRecordId],
  );

  const handleDeleteHotspotRow = useCallback(
    async (rowIndex: number, row: Record<string, unknown>) => {
      if (!activeRecord) return;
      const hotspotId = String((row as { id?: unknown }).id ?? "");
      const isDraft = Boolean((row as { __draft?: boolean }).__draft);

      if (isDraft) {
        if (hotspotId) {
          removeDraftHotspotRow(hotspotId);
        }
        setActiveRecord((prev) => {
          if (!prev?.data?.rows) return prev;
          const rows = [...prev.data.rows];
          rows.splice(rowIndex, 1);
          return {
            ...prev,
            rowCount: rows.length,
            data: {
              ...prev.data,
              rows,
              metadata: {
                ...(prev.data.metadata ?? {}),
                rowCount: rows.length,
              },
            },
          };
        });
        return;
      }

      if (!hotspotId) return;

      try {
        if (!user && !isLocalBackend) {
          throw new Error("You must be signed in to delete hotspots.");
        }
        if (isLocalBackend) {
          const craftspaceId = project?.craftspace_id || "default";
          await removeLocalHotspot(hotspotId, craftspaceId);
        } else {
          await convex.mutation(api.hotspots.remove, { id: hotspotId });
        }

        deleteHotspot(hotspotId);
        setActiveRecord((prev) => {
          if (!prev?.data?.rows) return prev;
          const rows = [...prev.data.rows];
          rows.splice(rowIndex, 1);
          return {
            ...prev,
            rowCount: rows.length,
            data: {
              ...prev.data,
              rows,
              metadata: {
                ...(prev.data.metadata ?? {}),
                rowCount: rows.length,
              },
            },
          };
        });

        if (projectId && project) {
          const craftspaceId = project.craftspace_id || "default";
          await CMSService.refreshHotspotsDataset(projectId, userId, craftspaceId);
          await loadCMSData({ silent: true });
          setActiveRecordRefreshKey((k) => k + 1);
        }
      } catch (error) {
        console.error("Failed to delete hotspot:", error);
        toast({
          variant: "destructive",
          title: "Delete failed",
          description: error instanceof Error ? error.message : "Failed to delete hotspot.",
        });
      }
    },
    [
      activeRecord,
      convex,
      deleteHotspot,
      isLocalBackend,
      loadCMSData,
      project,
      projectId,
      removeDraftHotspotRow,
      toast,
      user,
      userId,
    ],
  );

  const pasteCopiedPanoramaToRows = useCallback(
    async (rowIndices: number[]) => {
      const copied = copiedPanoramaRef.current;
      if (!copied) return;
      const resolvedPanorama =
        panoramas?.find((item) => item.id === copied.id) ??
        ({
          id: copied.id,
          name: copied.name || "Panorama",
          thumbnail_url: copied.thumbnail,
          thumbnail_url_webp: copied.thumbnail,
        } as Panorama);
      const uniqueIndices = Array.from(new Set(rowIndices));
      for (const rowIndex of uniqueIndices) {
        await handleLinkDraftPanorama(rowIndex, resolvedPanorama);
      }
    },
    [handleLinkDraftPanorama, panoramas],
  );

  const applyHotspotStyleToRows = useCallback(
    async (rowIndices: number[], styleValue: string) => {
      const presets = loadHotspotStylePresets();
      const uniqueIndices = Array.from(new Set(rowIndices));
      for (const rowIndex of uniqueIndices) {
        const row = activeRows[rowIndex];
        if (!row) continue;
        const hotspotId = String((row as { id?: unknown }).id ?? "");
        const isDraft = Boolean((row as { __draft?: unknown }).__draft);
        const nextStyle = styleValue.trim();

        updateActiveRow(rowIndex, { hotspot_style: nextStyle });

        const currentContent = parseRowContent(row);
        const hotspotType = typeof row.type === "string" ? row.type : "";
        const preset = presets.find((item) => item.id === nextStyle);
        const presetScale =
          preset?.scale && typeof preset.scale === "object"
            ? {
                x: typeof preset.scale.x === "number" ? preset.scale.x : 1,
                y: typeof preset.scale.y === "number" ? preset.scale.y : 1,
                z: typeof preset.scale.z === "number" ? preset.scale.z : 1,
              }
            : null;
        const nextContent = nextStyle
          ? preset
            ? mergeContentWithPreset(hotspotType, currentContent, preset)
            : { ...currentContent, [HOTSPOT_STYLE_PRESET_ID_KEY]: nextStyle }
          : (() => {
              const cleared = { ...currentContent };
              delete cleared[HOTSPOT_STYLE_PRESET_ID_KEY];
              return cleared;
            })();

        updateActiveRow(rowIndex, { content: JSON.stringify(nextContent) });
        if (presetScale) {
          updateActiveRow(rowIndex, {
            scale_x: presetScale.x,
            scale_y: presetScale.y,
            scale_z: presetScale.z,
          });
        }

        if (isDraft || !hotspotId) {
          if (hotspotId) {
            updateDraftHotspotRow(hotspotId, {
              hotspot_style: nextStyle,
              content: JSON.stringify(nextContent),
              ...(presetScale
                ? { scale_x: presetScale.x, scale_y: presetScale.y, scale_z: presetScale.z }
                : {}),
            });
          }
          continue;
        }

        updateHotspot(hotspotId, {
          content: nextContent,
          ...(presetScale ? { scale: presetScale } : {}),
        });

        try {
          if (!user && !isLocalBackend) {
            throw new Error("You must be signed in to update hotspot styles.");
          }
          if (isLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalHotspot({
              id: hotspotId,
              craftspace_id: craftspaceId,
              content: nextContent,
              ...(presetScale ? { scale: presetScale } : {}),
            });
          } else {
            await convex.mutation(api.hotspots.update, {
              id: hotspotId,
              content: nextContent,
              ...(presetScale ? { scale: presetScale } : {}),
            });
          }
        } catch (error) {
          console.error("Failed to update hotspot style:", error);
          toast({
            variant: "destructive",
            title: "Update failed",
            description:
              error instanceof Error ? error.message : "Failed to update hotspot style.",
          });
        }
      }
    },
    [
      activeRows,
      convex,
      isLocalBackend,
      loadHotspotStylePresets,
      mergeContentWithPreset,
      parseRowContent,
      toast,
      updateActiveRow,
      updateDraftHotspotRow,
      updateHotspot,
      user,
    ],
  );

  const findRowIndexById = useCallback(
    (rowId: string) =>
      activeRows.findIndex((row) => String((row as { id?: unknown }).id ?? "") === rowId),
    [activeRows],
  );

  const clearCellValue = useCallback(
    async (rowIndex: number, fieldName: "name" | "panorama_file" | "hotspot_style") => {
      const row = activeRows[rowIndex];
      if (!row) return;
      const rowId = String((row as { id?: unknown }).id ?? "");
      const isDraft = isDraftRow(row);

      if (fieldName === "name") {
        const prevName = typeof row.name === "string" ? row.name : "";
        lastClearActionRef.current = {
          rowId,
          rowIndex,
          fieldName,
          prevValues: { name: prevName },
        };
        updateActiveRow(rowIndex, { name: "" });
        if (isDraft && isHotspotsTable) {
          updateDraftHotspotRow(rowId, { name: "" });
          return;
        }
        if (!rowId) return;
        try {
          if (!user && !isLocalBackend) {
            throw new Error("You must be signed in to edit hotspot names.");
          }
          if (isHotspotsTable) {
            if (isLocalBackend) {
              const craftspaceId = project?.craftspace_id || "default";
              await updateLocalHotspot({ id: rowId, craftspace_id: craftspaceId, name: "" });
            } else {
              await convex.mutation(api.hotspots.update, { id: rowId, name: "" });
            }
            updateHotspot(rowId, { name: "" });
          }
          if (isPanoramasTable) {
            if (isLocalBackend) {
              const craftspaceId = project?.craftspace_id || "default";
              await updateLocalPanorama({ id: rowId, craftspace_id: craftspaceId, name: "" });
            } else {
              await convex.mutation(api.panoramas.update, { id: rowId, name: "" });
            }
            updatePanorama(rowId, { name: "" });
          }
        } catch (error) {
          console.error("Failed to clear name:", error);
          toast({
            variant: "destructive",
            title: "Clear failed",
            description: error instanceof Error ? error.message : "Failed to clear name.",
          });
        }
        return;
      }

      if (fieldName === "panorama_file" && isHotspotsTable) {
        const prevPanoramaId =
          typeof (row as { panorama_id?: unknown }).panorama_id === "string"
            ? String((row as { panorama_id?: unknown }).panorama_id)
            : "";
        const prevPanoramaName =
          typeof (row as { panorama_name?: unknown }).panorama_name === "string"
            ? String((row as { panorama_name?: unknown }).panorama_name)
            : "";
        const prevPanoramaFile =
          typeof (row as { panorama_file?: unknown }).panorama_file === "string"
            ? String((row as { panorama_file?: unknown }).panorama_file)
            : "";

        if (!isLocalBackend && !isDraft) {
          toast({
            variant: "destructive",
            title: "Clear failed",
            description: "Unlinking panoramas is only supported in the local backend right now.",
          });
          return;
        }

        lastClearActionRef.current = {
          rowId,
          rowIndex,
          fieldName,
          prevValues: {
            panorama_id: prevPanoramaId,
            panorama_name: prevPanoramaName,
            panorama_file: prevPanoramaFile,
          },
        };

        updateActiveRow(rowIndex, {
          panorama_id: "",
          panorama_name: "",
          panorama_file: "",
        });
        if (isDraft) {
          updateDraftHotspotRow(rowId, {
            panorama_id: "",
            panorama_name: "",
            panorama_file: "",
          });
          return;
        }
        if (!rowId) return;
        try {
          if (isLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalHotspot({ id: rowId, craftspace_id: craftspaceId, panorama_id: null });
          }
          updateHotspot(rowId, { panorama_id: "" });
        } catch (error) {
          console.error("Failed to clear panorama:", error);
          toast({
            variant: "destructive",
            title: "Clear failed",
            description:
              error instanceof Error ? error.message : "Failed to clear linked panorama.",
          });
        }
        return;
      }

      if (fieldName === "hotspot_style" && isHotspotsTable) {
        const prevStyle =
          typeof (row as { hotspot_style?: unknown }).hotspot_style === "string"
            ? String((row as { hotspot_style?: unknown }).hotspot_style)
            : "";
        const currentContent = parseRowContent(row);
        const nextContent = { ...currentContent };
        delete nextContent[HOTSPOT_STYLE_PRESET_ID_KEY];

        lastClearActionRef.current = {
          rowId,
          rowIndex,
          fieldName,
          prevValues: { hotspot_style: prevStyle },
          prevContent: currentContent,
        };

        updateActiveRow(rowIndex, {
          hotspot_style: "",
          content: JSON.stringify(nextContent),
        });
        if (isDraft) {
          updateDraftHotspotRow(rowId, {
            hotspot_style: "",
            content: JSON.stringify(nextContent),
          });
          return;
        }
        if (!rowId) return;
        try {
          if (!user && !isLocalBackend) {
            throw new Error("You must be signed in to edit hotspot styles.");
          }
          if (isLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalHotspot({ id: rowId, craftspace_id: craftspaceId, content: nextContent });
          } else {
            await convex.mutation(api.hotspots.update, { id: rowId, content: nextContent });
          }
          updateHotspot(rowId, { content: nextContent });
        } catch (error) {
          console.error("Failed to clear hotspot style:", error);
          toast({
            variant: "destructive",
            title: "Clear failed",
            description:
              error instanceof Error ? error.message : "Failed to clear hotspot style.",
          });
        }
      }
    },
    [
      activeRows,
      convex,
      isHotspotsTable,
      isLocalBackend,
      isPanoramasTable,
      parseRowContent,
      toast,
      updateActiveRow,
      updateDraftHotspotRow,
      updateHotspot,
      updatePanorama,
      user,
    ],
  );

  const restoreLastClear = useCallback(async () => {
    const last = lastClearActionRef.current;
    if (!last) return false;
    const rowIndex = findRowIndexById(last.rowId);
    if (rowIndex === -1) {
      lastClearActionRef.current = null;
      return false;
    }
    const row = activeRows[rowIndex];
    if (!row) {
      lastClearActionRef.current = null;
      return false;
    }
    const isDraft = isDraftRow(row);

    if (last.fieldName === "name") {
      const prevName = typeof last.prevValues.name === "string" ? last.prevValues.name : "";
      updateActiveRow(rowIndex, { name: prevName });
      if (isDraft && isHotspotsTable) {
        updateDraftHotspotRow(String(row.id ?? ""), { name: prevName });
        lastClearActionRef.current = null;
        return true;
      }
      const rowId = String((row as { id?: unknown }).id ?? "");
      if (rowId) {
        if (isHotspotsTable) {
          if (isLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalHotspot({ id: rowId, craftspace_id: craftspaceId, name: prevName });
          } else {
            await convex.mutation(api.hotspots.update, { id: rowId, name: prevName });
          }
          updateHotspot(rowId, { name: prevName });
        }
        if (isPanoramasTable) {
          if (isLocalBackend) {
            const craftspaceId = project?.craftspace_id || "default";
            await updateLocalPanorama({ id: rowId, craftspace_id: craftspaceId, name: prevName });
          } else {
            await convex.mutation(api.panoramas.update, { id: rowId, name: prevName });
          }
          updatePanorama(rowId, { name: prevName });
        }
      }
    }

    if (last.fieldName === "panorama_file" && isHotspotsTable) {
      const prevPanoramaId =
        typeof last.prevValues.panorama_id === "string" ? last.prevValues.panorama_id : "";
      const prevPanoramaName =
        typeof last.prevValues.panorama_name === "string" ? last.prevValues.panorama_name : "";
      const prevPanoramaFile =
        typeof last.prevValues.panorama_file === "string" ? last.prevValues.panorama_file : "";
      updateActiveRow(rowIndex, {
        panorama_id: prevPanoramaId,
        panorama_name: prevPanoramaName,
        panorama_file: prevPanoramaFile,
      });
      if (isDraft) {
        updateDraftHotspotRow(String(row.id ?? ""), {
          panorama_id: prevPanoramaId,
          panorama_name: prevPanoramaName,
          panorama_file: prevPanoramaFile,
        });
        lastClearActionRef.current = null;
        return true;
      }
      const rowId = String((row as { id?: unknown }).id ?? "");
      if (rowId && isLocalBackend) {
        const craftspaceId = project?.craftspace_id || "default";
        await updateLocalHotspot({ id: rowId, craftspace_id: craftspaceId, panorama_id: prevPanoramaId || null });
        updateHotspot(rowId, { panorama_id: prevPanoramaId });
      }
    }

    if (last.fieldName === "hotspot_style" && isHotspotsTable) {
      const prevStyle =
        typeof last.prevValues.hotspot_style === "string" ? last.prevValues.hotspot_style : "";
      const prevContent = last.prevContent ?? parseRowContent(row);
      updateActiveRow(rowIndex, {
        hotspot_style: prevStyle,
        content: JSON.stringify(prevContent),
      });
      if (isDraft) {
        updateDraftHotspotRow(String(row.id ?? ""), {
          hotspot_style: prevStyle,
          content: JSON.stringify(prevContent),
        });
        lastClearActionRef.current = null;
        return true;
      }
      const rowId = String((row as { id?: unknown }).id ?? "");
      if (rowId) {
        if (isLocalBackend) {
          const craftspaceId = project?.craftspace_id || "default";
          await updateLocalHotspot({ id: rowId, craftspace_id: craftspaceId, content: prevContent });
        } else {
          await convex.mutation(api.hotspots.update, { id: rowId, content: prevContent });
        }
        updateHotspot(rowId, { content: prevContent });
      }
    }

    lastClearActionRef.current = null;
    return true;
  }, [
    activeRows,
    convex,
    findRowIndexById,
    isHotspotsTable,
    isLocalBackend,
    isPanoramasTable,
    parseRowContent,
    updateActiveRow,
    updateDraftHotspotRow,
    updateHotspot,
    updatePanorama,
  ]);

  const getRowSelectionKey = useCallback(
    (row: Record<string, unknown>, index: number) => {
      const rowId = String((row as { id?: unknown }).id ?? "");
      return rowId ? rowId : `draft-${index}`;
    },
    [],
  );
  const getHotspotStyleTargetIndices = useCallback(
    (rowIndex: number) => {
      if (!isHotspotsTable) return [rowIndex];
      const selectedIndices = activeRows
        .map((row, index) => ({ index, key: getRowSelectionKey(row, index) }))
        .filter((entry) => selectedRowKeys.has(entry.key))
        .map((entry) => entry.index);
      if (selectedIndices.length === 0) return [rowIndex];
      const row = activeRows[rowIndex];
      if (!row) return [rowIndex];
      const rowKey = getRowSelectionKey(row, rowIndex);
      return selectedRowKeys.has(rowKey) ? selectedIndices : [rowIndex];
    },
    [activeRows, getRowSelectionKey, isHotspotsTable, selectedRowKeys],
  );
  const handleSelectRow = useCallback(
    (event: React.MouseEvent, rowIndex: number, row: Record<string, unknown>) => {
      const key = getRowSelectionKey(row, rowIndex);
      const isToggle = event.metaKey || event.ctrlKey;
      const isShift = event.shiftKey;

      if (isShift && lastSelectedRowIndexRef.current !== null) {
        const start = Math.min(lastSelectedRowIndexRef.current, rowIndex);
        const end = Math.max(lastSelectedRowIndexRef.current, rowIndex);
        const rangeKeys = new Set<string>();
        for (let i = start; i <= end; i += 1) {
          const rowItem = activeRows[i];
          if (!rowItem) continue;
          rangeKeys.add(getRowSelectionKey(rowItem, i));
        }
        setSelectedRowKeys(rangeKeys);
      } else if (isToggle) {
        setSelectedRowKeys((prev) => {
          const next = new Set(prev);
          if (next.has(key)) {
            next.delete(key);
          } else {
            next.add(key);
          }
          return next;
        });
      } else {
        setSelectedRowKeys(new Set([key]));
      }

      lastSelectedRowIndexRef.current = rowIndex;
    },
    [activeRows, getRowSelectionKey],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const key = event.key.toLowerCase();
      const isUndo = (event.metaKey || event.ctrlKey) && key === "z";
      const isCopy = (event.metaKey || event.ctrlKey) && key === "c";
      const isPaste = (event.metaKey || event.ctrlKey) && key === "v";
      const target = event.target as HTMLElement | null;

      if (target) {
        const tag = target.tagName;
        if (
          (tag === "INPUT" || tag === "TEXTAREA" || target.isContentEditable) &&
          !isUndo
        ) {
          return;
        }
      }

      if (isCopy) {
        if (!selectedCell) return;
        if (!(isHotspotsTable || isPanoramasTable)) return;
        const row = activeRows[selectedCell.rowIndex];
        if (!row) return;
        if (selectedCell.fieldName === "panorama_file") {
          const panoramaId = String((row as { panorama_id?: unknown }).panorama_id ?? "");
          if (!panoramaId) {
            toast({
              variant: "destructive",
              title: "Copy failed",
              description: "No linked panorama found in this cell.",
            });
            return;
          }
          const panorama = panoramas?.find((item) => item.id === panoramaId) ?? null;
          const panoramaName =
            typeof (row as { panorama_name?: unknown }).panorama_name === "string"
              ? String((row as { panorama_name?: unknown }).panorama_name)
              : panorama?.name ?? "";
          const panoramaFile =
            typeof (row as { panorama_file?: unknown }).panorama_file === "string"
              ? String((row as { panorama_file?: unknown }).panorama_file)
              : panorama?.thumbnail_url_webp || panorama?.thumbnail_url || "";
          event.preventDefault();
          copiedPanoramaRef.current = {
            id: panoramaId,
            name: panoramaName,
            thumbnail: panoramaFile,
          };
          toast({
            title: "Linked panorama copied",
            description: panoramaName || panoramaId,
          });
          return;
        }
        if (selectedCell.fieldName === "hotspot_style") {
          const styleValue =
            typeof (row as { hotspot_style?: unknown }).hotspot_style === "string"
              ? String((row as { hotspot_style?: unknown }).hotspot_style)
              : "";
          const styleName = styleValue ? hotspotStyleNameById.get(styleValue) ?? "" : "";
          event.preventDefault();
          copiedHotspotStyleRef.current = styleValue;
          if (styleName || styleValue) {
            void navigator.clipboard?.writeText(styleName || styleValue);
          }
          return;
        }
        // Custom field copy – put cell value on system clipboard
        const cellValue = row[selectedCell.fieldName];
        const textValue =
          cellValue === null || cellValue === undefined ? "" : String(cellValue);
        event.preventDefault();
        void navigator.clipboard?.writeText(textValue);
        return;
      }

      if (isPaste) {
        if (!(isHotspotsTable || isPanoramasTable)) return;
        const selectedIndices = activeRows
          .map((row, idx) => ({
            index: idx,
            key: getRowSelectionKey(row, idx),
          }))
          .filter((entry) => selectedRowKeys.has(entry.key))
          .map((entry) => entry.index);

        const targetIndices =
          selectedIndices.length > 0 ? selectedIndices : selectedCell ? [selectedCell.rowIndex] : [];

        if (targetIndices.length === 0) return;

        if (selectedCell?.fieldName === "panorama_file") {
          const panorama = copiedPanoramaRef.current;
          if (!panorama) {
            toast({
              variant: "destructive",
              title: "Paste failed",
              description: "Copy a linked panorama first.",
            });
            return;
          }
          event.preventDefault();
          suppressHotspotRefreshRef.current = true;
          void (async () => {
            try {
              await pasteCopiedPanoramaToRows(targetIndices);
            } finally {
              suppressHotspotRefreshRef.current = false;
            }
          })();
          return;
        }

        if (selectedCell?.fieldName === "hotspot_style") {
          if (copiedHotspotStyleRef.current === null) {
            toast({
              variant: "destructive",
              title: "Paste failed",
              description: "Copy a hotspot style first.",
            });
            return;
          }
          event.preventDefault();
          suppressHotspotRefreshRef.current = true;
          void (async () => {
            try {
              await applyHotspotStyleToRows(targetIndices, copiedHotspotStyleRef.current || "");
            } finally {
              suppressHotspotRefreshRef.current = false;
            }
          })();
          return;
        }

        // Custom field paste – read from system clipboard and apply to selected rows
        if (selectedCell) {
          event.preventDefault();
          void (async () => {
            try {
              const clipText = await navigator.clipboard.readText();
              if (!clipText) return;
              const fields =
                (activeRecord?.data?.fields as FieldStructure[] | undefined) ||
                (activeRecord?.fieldStructure as FieldStructure[] | undefined) ||
                [];
              const fieldDef = fields.find((f) => f.name === selectedCell.fieldName);
              if (!fieldDef) return;
              const rows = (activeRecord?.data?.rows ?? []) as Array<Record<string, unknown>>;
              const nextRows = [...rows];
              const lines = clipText
                .split(/\r?\n/)
                .map((l) => l.trim())
                .filter(Boolean);
              if (lines.length > 1) {
                // Multi-line paste: fill downward from the selected cell
                const startIdx = selectedCell.rowIndex;
                for (let i = 0; i < lines.length; i++) {
                  const rowIdx = startIdx + i;
                  if (rowIdx >= nextRows.length) break;
                  nextRows[rowIdx] = {
                    ...nextRows[rowIdx],
                    [selectedCell.fieldName]: coerceFieldValue(lines[i], fieldDef.fieldType),
                  };
                }
              } else {
                // Single value: paste into all selected rows
                const val = coerceFieldValue(clipText.trim(), fieldDef.fieldType);
                for (const idx of targetIndices) {
                  if (idx >= nextRows.length) continue;
                  nextRows[idx] = {
                    ...nextRows[idx],
                    [selectedCell.fieldName]: val,
                  };
                }
              }
              const nextData = {
                ...(activeRecord?.data ?? { fields, rows: [] }),
                fields,
                rows: nextRows,
                metadata: {
                  ...(activeRecord?.data?.metadata ?? {}),
                  rowCount: nextRows.length,
                  fieldCount: fields.length,
                },
              };
              const nextRecord: CMSDataRecord = { ...activeRecord!, data: nextData };
              setActiveRecord(nextRecord);
              await persistDatasetChanges(fields, nextRows);
            } catch {
              // clipboard access denied – ignore
            }
          })();
        }
        return;
      }

      if (isUndo) {
        if (lastClearActionRef.current) {
          event.preventDefault();
          event.stopPropagation();
          void restoreLastClear();
          return;
        }
        if (!isHotspotsActive || !selectedCell) return;
        const row = activeRows[selectedCell.rowIndex];
        if (!row) return;
        if (!isDraftRow(row)) return;
        event.preventDefault();
        event.stopPropagation();
        handleDeleteHotspotRow(selectedCell.rowIndex, row);
        setEditingCell(null);
        setSelectedCell(null);
        setSelectedRowKeys(new Set());
        lastSelectedRowIndexRef.current = null;
        return;
      }

      if (event.shiftKey && event.key === "Enter") {
        if (!isHotspotsActive || !selectedCell || editingCell) return;
        event.preventDefault();
        insertDraftHotspotRow(selectedCell.rowIndex);
        return;
      }

      if (event.key !== "Delete" && event.key !== "Backspace") return;
      if (!selectedCell || editingCell) return;

      const row = activeRows[selectedCell.rowIndex];
      if (!row) return;

      event.preventDefault();
      if (
        selectedCell.fieldName === "name" ||
        selectedCell.fieldName === "panorama_file" ||
        selectedCell.fieldName === "hotspot_style"
      ) {
        const selectedIndices = activeRows
          .map((r, i) => ({ index: i, key: getRowSelectionKey(r, i) }))
          .filter((e) => selectedRowKeys.has(e.key))
          .map((e) => e.index);
        const targetIndices =
          selectedIndices.length > 0 ? selectedIndices : [selectedCell.rowIndex];
        for (const idx of targetIndices) {
          void clearCellValue(idx, selectedCell.fieldName);
        }
      } else {
        // Clear custom field cells for all selected rows
        const fields =
          (activeRecord?.data?.fields as FieldStructure[] | undefined) ||
          (activeRecord?.fieldStructure as FieldStructure[] | undefined) ||
          [];
        const fieldDef = fields.find((f) => f.name === selectedCell.fieldName);
        if (fieldDef) {
          const selectedIndices = activeRows
            .map((r, i) => ({ index: i, key: getRowSelectionKey(r, i) }))
            .filter((e) => selectedRowKeys.has(e.key))
            .map((e) => e.index);
          const targetIndices =
            selectedIndices.length > 0 ? selectedIndices : [selectedCell.rowIndex];
          const rows = (activeRecord?.data?.rows ?? []) as Array<Record<string, unknown>>;
          const nextRows = [...rows];
          for (const idx of targetIndices) {
            if (idx >= nextRows.length) continue;
            nextRows[idx] = { ...nextRows[idx], [selectedCell.fieldName]: null };
          }
          const nextData = {
            ...(activeRecord?.data ?? { fields, rows: [] }),
            fields,
            rows: nextRows,
            metadata: {
              ...(activeRecord?.data?.metadata ?? {}),
              rowCount: nextRows.length,
              fieldCount: fields.length,
            },
          };
          const nextRecord: CMSDataRecord = { ...activeRecord!, data: nextData };
          setActiveRecord(nextRecord);
          void persistDatasetChanges(fields, nextRows);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeRecord,
    activeRows,
    coerceFieldValue,
    convex,
    editingCell,
    getRowSelectionKey,
    handleDeleteHotspotRow,
    insertDraftHotspotRow,
    isHotspotsActive,
    isHotspotsTable,
    isLocalBackend,
    isPanoramasTable,
    applyHotspotStyleToRows,
    clearCellValue,
    pasteCopiedPanoramaToRows,
    panoramas,
    persistDatasetChanges,
    restoreLastClear,
    selectedCell,
    selectedRowKeys,
    toast,
    updateActiveRow,
    updateDraftHotspotRow,
    updateHotspot,
    updatePanorama,
    user,
  ]);

  useEffect(() => {
    if (!selectedCell || selectedCell.fieldName !== "hotspot_style") {
      setHotspotStylePopoverRow(null);
    }
  }, [selectedCell]);

  const handleDeleteSelectedHotspots = useCallback(async () => {
    if (!activeRecord || !isHotspotsActive) return;
    if (selectedRowKeys.size === 0) return;

    const rowsToDelete = activeRows
      .map((row, index) => ({
        row,
        index,
        key: getRowSelectionKey(row, index),
      }))
      .filter((entry) => selectedRowKeys.has(entry.key));

    if (rowsToDelete.length === 0) return;

    const indicesToRemove: number[] = [];
    const failures: string[] = [];

    for (const entry of rowsToDelete) {
      const row = entry.row;
      const rowIndex = entry.index;
      const hotspotId = String((row as { id?: unknown }).id ?? "");
      const isDraft = Boolean((row as { __draft?: boolean }).__draft);

      if (isDraft || !hotspotId) {
        if (hotspotId) {
          removeDraftHotspotRow(hotspotId);
        }
        indicesToRemove.push(rowIndex);
        continue;
      }

      try {
        if (!user && !isLocalBackend) {
          throw new Error("You must be signed in to delete hotspots.");
        }
        if (isLocalBackend) {
          const craftspaceId = project?.craftspace_id || "default";
          await removeLocalHotspot(hotspotId, craftspaceId);
        } else {
          await convex.mutation(api.hotspots.remove, { id: hotspotId });
        }
        deleteHotspot(hotspotId);
        indicesToRemove.push(rowIndex);
      } catch (error) {
        console.error("Failed to delete hotspot:", error);
        failures.push(hotspotId || "unknown");
      }
    }

    if (indicesToRemove.length > 0) {
      const sorted = [...indicesToRemove].sort((a, b) => b - a);
      setActiveRecord((prev) => {
        if (!prev?.data?.rows) return prev;
        const rows = [...prev.data.rows];
        for (const idx of sorted) {
          rows.splice(idx, 1);
        }
        return {
          ...prev,
          rowCount: rows.length,
          data: {
            ...prev.data,
            rows,
            metadata: {
              ...(prev.data.metadata ?? {}),
              rowCount: rows.length,
            },
          },
        };
      });
    }

    setSelectedRowKeys(new Set());
    lastSelectedRowIndexRef.current = null;

    if (failures.length > 0) {
      toast({
        variant: "destructive",
        title: "Delete failed",
        description: "Some hotspots could not be deleted.",
      });
    }
  }, [
    activeRecord,
    activeRows,
    convex,
    deleteHotspot,
    getRowSelectionKey,
    isHotspotsActive,
    isLocalBackend,
    removeDraftHotspotRow,
    selectedRowKeys,
    toast,
    user,
  ]);

  const getFieldTypeColor = (fieldType: string) => {
    switch (fieldType) {
      case "text":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "longtext":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "number":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "url":
        return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300";
      case "date":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "switch":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
  };

  const handleUpdateDataset = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!project?.craftspace_id || !updateRecord) return;

    const file = event.target.files?.[0];
    if (!file) return;

    if (!mergeField) {
      toast({
        variant: "destructive",
        title: "Merge field required",
        description: "Please select a field to use for merging records.",
      });
      return;
    }

    try {
      setIsUploading(true);

      const updatedRecord = await CMSService.updateDatasetWithCsv(
        updateRecord.id,
        file,
        project.id,
        userId,
        project.craftspace_id,
        {
          mergeField,
          updateExisting,
          addNew,
        },
      );

      // Update the record in the list
      setCmsRecords((prev) => prev.map((r) => (r.id === updatedRecord.id ? updatedRecord : r)));

      toast({
        title: "Dataset updated",
        description: `Successfully updated "${updatedRecord.name}" with ${file.name}`,
      });

      // Reset form
      setUpdateDialogOpen(false);
      setUpdateRecord(null);
      setMergeField("");
      setUpdateExisting(true);
      setAddNew(true);

      if (updateFileInputRef.current) {
        updateFileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Error updating dataset:", error);
      toast({
        variant: "destructive",
        title: "Update failed",
        description: error instanceof Error ? error.message : "Failed to update dataset.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const openUpdateDialog = (record: CMSDataRecord) => {
    setUpdateRecord(record);
    setMergeField("");
    setUpdateDialogOpen(true);
  };

  // Auto-generate panoramas and hotspots datasets on page load and when they change
  // biome-ignore lint/correctness/useHookAtTopLevel: All returns are above this
  useEffect(() => {
    const autoGenerateDatasets = async () => {
      if (!projectId || !project) return;
      if (!user && !isLocalBackend) return;

      try {
        // Get craftspace ID from project
        const craftspaceId = project.craftspace_id || "default";

        // Generate or refresh panoramas dataset
        await CMSService.refreshPanoramasDataset(projectId, userId, craftspaceId);
        // Reload CMS data to show the new/updated dataset
        await loadCMSData({ silent: true });
        setActiveRecordRefreshKey((k) => k + 1);
      } catch (error) {
        console.warn("❌ Failed to auto-generate panoramas dataset:", error);
      }
    };

    // Run when project loads or panoramas change
    autoGenerateDatasets();
  }, [projectId, user, project, loadCMSData]); // Watch for project changes

  // Auto-generate hotspots dataset when hotspots change
  // biome-ignore lint/correctness/useHookAtTopLevel: All returns are above this
  useEffect(() => {
    const autoGenerateHotspotsDataset = async () => {
      if (suppressHotspotRefreshRef.current) return;
      if (!projectId || !project || !hotspots) return;
      if (!user && !isLocalBackend) return;

      try {
        // Get craftspace ID from project
        const craftspaceId = project.craftspace_id || "default";

        // Generate or refresh hotspots dataset
        await CMSService.refreshHotspotsDataset(projectId, userId, craftspaceId);

        // Reload CMS data to show the new/updated dataset
        await loadCMSData();
        setActiveRecordRefreshKey((k) => k + 1);

      } catch (error) {
        console.warn("Failed to auto-generate hotspots dataset:", error);
      }
    };

    // Debounce the auto-generation to prevent excessive API calls
    const timeoutId = setTimeout(() => {
      autoGenerateHotspotsDataset();
    }, 2000); // 2 second debounce to give more time for updates

    return () => clearTimeout(timeoutId);
  }, [projectId, user, project, hotspots, loadCMSData]); // Simplified dependency array

  // Watch for individual hotspot changes by creating a content hash
  // biome-ignore lint/correctness/useHookAtTopLevel: All returns are above this
  const hotspotContentHash = useMemo(() => {
    if (!hotspots || hotspots.length === 0) return "";
    return hotspots
      .map((h) => `${h.id}-${h.name}-${h.visible}-${JSON.stringify(h.content)}`)
      .join("|");
  }, [hotspots]);

  // Trigger refresh when hotspot content changes
  // biome-ignore lint/correctness/useHookAtTopLevel: All returns are above this
  useEffect(() => {
    if (!hotspotContentHash) return;
    if (suppressHotspotRefreshRef.current) return;

    console.log("🔍 Hotspot content hash changed, triggering refresh:", {
      hash: hotspotContentHash,
      hotspotCount: hotspots?.length || 0,
    });

    // Force a refresh by calling the refresh function directly
    const refreshHotspotsCollection = async () => {
      if (!projectId || !project) return;
      if (!user && !isLocalBackend) return;

      try {
        const craftspaceId = project.craftspace_id || "default";
        await CMSService.refreshHotspotsDataset(projectId, userId, craftspaceId);
        await loadCMSData();
        setActiveRecordRefreshKey((k) => k + 1);
      } catch (error) {
        console.warn("Failed to refresh hotspots collection:", error);
      }
    };

    // Debounce the refresh
    const timeoutId = setTimeout(refreshHotspotsCollection, 1500);
    return () => clearTimeout(timeoutId);
  }, [hotspotContentHash, projectId, user, project, hotspots, loadCMSData]);

  const orderedCmsRecords = useMemo(() => orderCMSRecords(cmsRecords), [cmsRecords]);

  useEffect(() => {
    attemptedHotspotsRefresh.current = false;
    setIsTableMenuOpen(false);
    setSelectedCell(null);
  }, [activeRecordId]);

  useEffect(() => {
    if (
      attemptedHotspotsRefresh.current ||
      !activeRecord ||
      !projectId ||
      !project ||
      !userId
    ) {
      return;
    }

    const fields = activeRecord.data?.fields ?? activeRecord.fieldStructure ?? [];
    const hasPanoramaFileField = fields.some((field: FieldStructure) => field.name === "panorama_file");
    if (hasPanoramaFileField) return;
    const craftspaceId = project.craftspace_id || "default";
    if (
      activeRecord.name === HOTSPOTS_COLLECTION_NAME ||
      activeRecord.name === LEGACY_HOTSPOTS_COLLECTION_NAME
    ) {
      attemptedHotspotsRefresh.current = true;
      void CMSService.refreshHotspotsDataset(projectId, userId, craftspaceId)
        .then(async () => { await loadCMSData({ silent: true }); setActiveRecordRefreshKey((k) => k + 1); })
        .catch((error) => {
          console.warn("Failed to refresh hotspots dataset for panorama thumbnails:", error);
        });
    }
    if (
      activeRecord.name === PANORAMAS_COLLECTION_NAME ||
      activeRecord.name === LEGACY_PANORAMAS_COLLECTION_NAME
    ) {
      attemptedHotspotsRefresh.current = true;
      void CMSService.refreshPanoramasDataset(projectId, userId, craftspaceId)
        .then(async () => { await loadCMSData({ silent: true }); setActiveRecordRefreshKey((k) => k + 1); })
        .catch((error) => {
          console.warn("Failed to refresh panoramas dataset for panorama thumbnails:", error);
        });
    }
  }, [activeRecord, projectId, project, userId, loadCMSData]);

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 pt-4 pb-1">
        <div className="flex items-center gap-2 mb-2">
          <Database className="h-5 w-5 text-white" />
          <h2 className="text-lg font-semibold text-white">Content Management</h2>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-4 pb-4 pt-1 space-y-4 flex flex-col">
        {/* Data Grid */}
        <div className="-mx-4 bg-[#374151] px-4 border-t border-gray-600/60 h-10">
          <div className="flex items-stretch gap-2 h-full">
            <div className="flex items-stretch gap-1 overflow-x-auto h-full no-scrollbar">
              {orderedCmsRecords.length === 0 ? (
                <span className="text-xs text-gray-200">No collections yet</span>
              ) : (
                orderedCmsRecords.map((record) => {
                  const isActive = activeRecordId === record.id;
                  const tabClass = `h-full px-3 rounded-md text-xs border transition-colors flex items-center gap-1 relative ${
                    isActive
                      ? "bg-[#1F2937] text-white border-gray-500 border-b-0 -mb-[2px] rounded-b-none z-20"
                      : "bg-transparent text-gray-300 border-gray-700/60 hover:border-gray-600 hover:text-white hover:bg-gray-700/40 hover:border-b-0 hover:rounded-b-none"
                  }`;
                  return (
                    <div key={record.id}>
                      {isActive ? (
                        <DropdownMenu open={isTableMenuOpen} onOpenChange={setIsTableMenuOpen}>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className={tabClass}
                              onContextMenu={(event) => {
                                event.preventDefault();
                                setIsTableMenuOpen(true);
                              }}
                            >
                              <span
                                aria-hidden
                                className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#1F2937] pointer-events-none"
                              />
                              <span>{record.name}</span>
                              <span className="text-[10px] text-gray-400">
                                {record.rowCount ?? 0}
                              </span>
                              <ChevronDown className="h-3 w-3 text-gray-300" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="start"
                            className="bg-gray-800 border-gray-700 text-gray-200"
                          >
                            <DropdownMenuItem onClick={() => handleExportData(record)}>
                              <Download className="h-3 w-3 mr-2" />
                              Download CSV
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleRenameRecord(record)}
                              disabled={isAutoGenerated(record)}
                            >
                              <Pencil className="h-3 w-3 mr-2" />
                              Rename table
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleDeleteRecord(record.id)}
                              disabled={isAutoGenerated(record)}
                              className="text-red-400 focus:text-red-300"
                            >
                              <Trash2 className="h-3 w-3 mr-2" />
                              Delete table
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setActiveRecordId(record.id);
                            setShowAddImport(false);
                          }}
                          className={tabClass}
                        >
                          <span>{record.name}</span>
                          <span className="text-[10px] text-gray-400">
                            {record.rowCount ?? 0}
                          </span>
                        </button>
                      )}
                    </div>
                  );
                })
              )}
              <DropdownMenu open={showAddImport} onOpenChange={setShowAddImport}>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="h-full px-3 rounded-md text-xs border border-transparent text-gray-300 flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" />
                    Add or import
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-56 bg-gray-800 border-gray-700 text-gray-200"
                >
                  <DropdownMenuLabel className="text-[11px] text-gray-400">
                    Blank table
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => setShowAddImport(false)}>
                    Start from scratch
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-gray-700" />
                  <DropdownMenuLabel className="text-[11px] text-gray-400">
                    Import
                  </DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
                    <Upload className="h-3 w-3 mr-2" />
                    CSV file
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled>
                    <FileText className="h-3 w-3 mr-2" />
                    Airtable
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {activeRecord && isAutoGenerated(activeRecord) && (
              <div className="ml-auto flex items-center gap-3 pr-1 text-xs text-gray-200">
                <span>Show all fields</span>
                <Switch checked={showAllFields} onCheckedChange={setShowAllFields} />
              </div>
            )}
          </div>
        </div>
        <Card className="bg-gray-800 border-0 flex-1 min-h-0">
          <CardContent className="p-0 h-full">
            {!hasLoadedOnce && (isLoading || isActiveLoading) ? (
              <div className="flex items-center justify-center py-8">
                <div className="flex items-center gap-2 text-gray-400">
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm">Loading...</span>
                </div>
              </div>
            ) : !activeRecord ? (
              <div className="flex flex-col items-center justify-center py-10">
                <Database className="h-10 w-10 text-gray-500 mb-3" />
                <h3 className="text-white text-sm font-medium mb-2">No Data Loaded</h3>
                <p className="text-gray-400 text-xs text-center">
                  Import a CSV or JSON file to get started.
                </p>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                <div className="flex-1 min-h-0 overflow-auto border border-gray-700 rounded-lg">
                  <Table className="w-max table-fixed">
                  <TableHeader className="bg-gray-800">
                    <TableRow className="border-gray-700 hover:bg-transparent">
                      <TableHead className="sticky top-0 z-10 bg-gray-800 text-[11px] text-gray-400 w-10 transition-colors hover:bg-muted/5">
                        #
                      </TableHead>
                      {displayedFields.map((field: FieldStructure) => {
                        const customWidth = columnWidths[field.name];
                        const widthValue =
                          customWidth ?? getDefaultColumnWidth(field, activeRecord?.name);
                        const widthStyle = { width: widthValue, minWidth: widthValue };
                        const isDeletable = isCustomField(field, activeRecord?.name);
                        const headerCell = (
                          <TableHead
                            key={field.name}
                            style={widthStyle}
                            className="sticky top-0 z-10 bg-gray-800 text-[11px] text-gray-400 whitespace-nowrap transition-colors hover:bg-muted/5 relative pr-2"
                          >
                            <span>{getFieldLabel(field, activeRecord?.name)}</span>
                            <div
                              role="separator"
                              aria-orientation="vertical"
                              onMouseDown={(event) => handleResizeStart(field.name, event)}
                              className="absolute right-0 top-1/2 -translate-y-1/2 h-4 w-1 cursor-col-resize rounded bg-gray-600/70 hover:bg-gray-400"
                            />
                          </TableHead>
                        );

                        if (!isDeletable) {
                          return headerCell;
                        }

                        return (
                          <ContextMenu key={field.name}>
                            <ContextMenuTrigger asChild>{headerCell}</ContextMenuTrigger>
                            <ContextMenuContent className="bg-gray-800 border-gray-700 text-gray-200">
                              {(field.fieldType === "date" || field.fieldType === "datetime" || field.fieldType === "singleselect") && (
                                <ContextMenuItem
                                  onClick={() => handleEditFieldOpen(field)}
                                >
                                  Edit field
                                </ContextMenuItem>
                              )}
                              <ContextMenuItem
                                onClick={() => handleDeleteCustomField(field)}
                                className="text-red-300 hover:text-red-200"
                              >
                                Delete field
                              </ContextMenuItem>
                            </ContextMenuContent>
                          </ContextMenu>
                        );
                    })}
                      {activeRecord &&
                        isAutoGenerated(activeRecord) &&
                        (isHotspotsTable || isPanoramasTable) && (
                          <TableHead className="sticky top-0 z-10 bg-gray-800 text-[11px] text-gray-300 w-10">
                            <Dialog open={addFieldDialogOpen} onOpenChange={setAddFieldDialogOpen}>
                              <DialogTrigger asChild>
                                <button
                                  type="button"
                                  disabled={isSavingCustomField}
                                  className="h-6 w-6 rounded border border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50"
                                  aria-label="Add field"
                                >
                                  +
                                </button>
                              </DialogTrigger>
                              <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
                                <DialogHeader>
                                  <DialogTitle className="text-white">Add Custom Field</DialogTitle>
                                  <DialogDescription className="text-gray-400">
                                    Add a custom field to this auto-generated table.
                                  </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label className="text-gray-300 text-sm">Field Name</Label>
                                    <Input
                                      value={newFieldName}
                                      onChange={(event) => setNewFieldName(event.target.value)}
                                      placeholder="e.g. long_description"
                                      className="bg-gray-700 border-gray-600 text-white"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-300 text-sm">
                                      Display Name (Optional)
                                    </Label>
                                    <Input
                                      value={newFieldDisplayName}
                                      onChange={(event) =>
                                        setNewFieldDisplayName(event.target.value)
                                      }
                                      placeholder="e.g. Long Description"
                                      className="bg-gray-700 border-gray-600 text-white"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-gray-300 text-sm">Field Type</Label>
                                    <select
                                      value={newFieldType}
                                      onChange={(event) => {
                                        setNewFieldType(event.target.value);
                                        if (event.target.value === "date") {
                                          setNewDateOptions({ ...DEFAULT_DATE_OPTIONS });
                                        }
                                        if (event.target.value !== "singleselect") {
                                          setNewFieldOptions([]);
                                          setNewFieldOptionInput("");
                                        }
                                      }}
                                      className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
                                    >
                                      <option value="text">Text</option>
                                      <option value="longtext">Long text</option>
                                      <option value="number">Number</option>
                                      <option value="url">URL</option>
                                      <option value="date">Date</option>
                                      <option value="switch">Switch</option>
                                      <option value="singleselect">Single Select</option>
                                    </select>
                                  </div>
                                  {newFieldType === "singleselect" && (
                                    <div>
                                      <Label className="text-gray-300 text-sm">Options</Label>
                                      <div className="space-y-1 mt-1">
                                        {newFieldOptions.map((opt) => (
                                          <div key={opt} className="flex items-center gap-1">
                                            <span className="flex-1 text-xs text-gray-200 bg-gray-700 px-2 py-1 rounded">{opt}</span>
                                            <button
                                              type="button"
                                              onClick={() => setNewFieldOptions((prev) => prev.filter((o) => o !== opt))}
                                              className="text-gray-400 hover:text-red-400 text-xs px-1"
                                            >✕</button>
                                          </div>
                                        ))}
                                        <div className="flex gap-1">
                                          <input
                                            type="text"
                                            value={newFieldOptionInput}
                                            onChange={(e) => setNewFieldOptionInput(e.target.value)}
                                            onKeyDown={(e) => {
                                              if (e.key === "Enter" && newFieldOptionInput.trim()) {
                                                e.preventDefault();
                                                const val = newFieldOptionInput.trim();
                                                if (!newFieldOptions.includes(val)) {
                                                  setNewFieldOptions((prev) => [...prev, val]);
                                                }
                                                setNewFieldOptionInput("");
                                              }
                                            }}
                                            placeholder="Add option..."
                                            className="flex-1 px-2 py-1 text-xs bg-gray-700 border border-gray-600 text-white rounded focus:outline-none"
                                          />
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const val = newFieldOptionInput.trim();
                                              if (val && !newFieldOptions.includes(val)) {
                                                setNewFieldOptions((prev) => [...prev, val]);
                                              }
                                              setNewFieldOptionInput("");
                                            }}
                                            className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded"
                                          >Add</button>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                  {newFieldType === "date" && (
                                    <>
                                      <p className="text-xs text-gray-400">
                                        Enter a date (e.g. 11/12/2023) or choose one from a calendar.
                                      </p>
                                      <div>
                                        <Label className="text-gray-300 text-sm">Date format</Label>
                                        <select
                                          value={newDateOptions.dateFormat}
                                          onChange={(e) => {
                                            const fmt = e.target.value as DateFieldOptions["dateFormat"];
                                            setNewDateOptions((prev) => ({
                                              ...prev,
                                              dateFormat: fmt,
                                              timeFormat: fmt === "us" ? "12h" : "24h",
                                            }));
                                          }
                                          }
                                          className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
                                        >
                                          <option value="local">
                                            Local ({new Date().toLocaleDateString()})
                                          </option>
                                          <option value="friendly">
                                            Friendly ({`${new Date().getDate()} ${new Date().toLocaleString("en-US", { month: "long" })} ${new Date().getFullYear()}`})
                                          </option>
                                          <option value="us">
                                            US ({`${new Date().getMonth() + 1}/${new Date().getDate()}/${new Date().getFullYear()}`})
                                          </option>
                                          <option value="european">
                                            European ({`${new Date().getDate()}/${new Date().getMonth() + 1}/${new Date().getFullYear()}`})
                                          </option>
                                          <option value="iso">
                                            ISO ({new Date().toISOString().slice(0, 10)})
                                          </option>
                                        </select>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <Label className="text-gray-300 text-sm">Include time</Label>
                                        <Switch
                                          checked={newDateOptions.includeTime}
                                          onCheckedChange={(checked) =>
                                            setNewDateOptions((prev) => ({
                                              ...prev,
                                              includeTime: checked,
                                            }))
                                          }
                                        />
                                      </div>
                                      {newDateOptions.includeTime && (
                                        <>
                                          <div>
                                            <Label className="text-gray-300 text-sm">Time format</Label>
                                            <select
                                              value={newDateOptions.timeFormat}
                                              onChange={(e) =>
                                                setNewDateOptions((prev) => ({
                                                  ...prev,
                                                  timeFormat: e.target.value as "12h" | "24h",
                                                }))
                                              }
                                              className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
                                            >
                                              <option value="12h">
                                                12 hour ({new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })})
                                              </option>
                                              <option value="24h">
                                                24 hour ({`${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`})
                                              </option>
                                            </select>
                                          </div>
                                          <div className="flex items-center justify-between">
                                            <Label className="text-gray-300 text-sm">
                                              Use the same time zone for all collaborators
                                            </Label>
                                            <Switch
                                              checked={newDateOptions.useSharedTimezone}
                                              onCheckedChange={(checked) =>
                                                setNewDateOptions((prev) => ({
                                                  ...prev,
                                                  useSharedTimezone: checked,
                                                }))
                                              }
                                            />
                                          </div>
                                          {newDateOptions.useSharedTimezone && (
                                            <div>
                                              <Label className="text-gray-300 text-sm">Time zone</Label>
                                              <select
                                                value={newDateOptions.timezone}
                                                onChange={(e) =>
                                                  setNewDateOptions((prev) => ({
                                                    ...prev,
                                                    timezone: e.target.value,
                                                  }))
                                                }
                                                className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
                                              >
                                                <option value="GMT/UTC">GMT/UTC</option>
                                                <option value="America/New_York">America/New_York (ET)</option>
                                                <option value="America/Chicago">America/Chicago (CT)</option>
                                                <option value="America/Denver">America/Denver (MT)</option>
                                                <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                                                <option value="Europe/London">Europe/London (GMT/BST)</option>
                                                <option value="Europe/Paris">Europe/Paris (CET)</option>
                                                <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                                                <option value="Europe/Madrid">Europe/Madrid (CET)</option>
                                                <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                                                <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                                                <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                                                <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                                              </select>
                                            </div>
                                          )}
                                          <div className="flex items-center justify-between">
                                            <Label className="text-gray-300 text-sm">Display time zone</Label>
                                            <Switch
                                              checked={newDateOptions.displayTimezone}
                                              onCheckedChange={(checked) =>
                                                setNewDateOptions((prev) => ({
                                                  ...prev,
                                                  displayTimezone: checked,
                                                }))
                                              }
                                            />
                                          </div>
                                        </>
                                      )}
                                      <div className="flex items-center justify-between">
                                        <Label className="text-gray-300 text-sm">
                                          Default to{" "}
                                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-700 text-gray-200 text-xs">
                                            <CalendarIcon className="h-3 w-3" /> Current date
                                          </span>
                                        </Label>
                                        <Switch
                                          checked={newDateOptions.defaultToCurrentDate}
                                          onCheckedChange={(checked) =>
                                            setNewDateOptions((prev) => ({
                                              ...prev,
                                              defaultToCurrentDate: checked,
                                            }))
                                          }
                                        />
                                      </div>
                                    </>
                                  )}
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      onClick={() => setAddFieldDialogOpen(false)}
                                      className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={handleAddCustomField}
                                      disabled={isSavingCustomField}
                                      className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                      {isSavingCustomField ? "Saving..." : "Add field"}
                                    </Button>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          </TableHead>
                        )}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {activeRows.length === 0 && isPanoramasTable ? (
                      <TableRow className="border-transparent hover:bg-transparent">
                        <TableCell
                          colSpan={displayedFields.length + 1 + (showAddFieldColumn ? 1 : 0)}
                          className="text-center py-10"
                        >
                          <span className="text-gray-400 text-sm">No panoramas yet. </span>
                          <button
                            type="button"
                            className="text-sm text-blue-400 hover:text-blue-300 underline underline-offset-2"
                            onClick={() => navigate(`/${projectId}/assets`)}
                          >
                            Upload panoramas →
                          </button>
                        </TableCell>
                      </TableRow>
                    ) : activeRows.length === 0 ? null : (
                      activeRows.map((row, index) => {
                        const rawRowId = String((row as { id?: unknown }).id ?? "");
                        const rowKey = rawRowId ? rawRowId : `row-${index}`;
                        const selectionKey = getRowSelectionKey(row, index);
                        const isRowSelected = selectedRowKeys.has(selectionKey);
                        const canClearCell =
                          selectedCell?.rowIndex === index &&
                          ((selectedCell.fieldName === "name" &&
                            (isHotspotsTable || isPanoramasTable)) ||
                            (selectedCell.fieldName === "panorama_file" && isHotspotsTable) ||
                            (selectedCell.fieldName === "hotspot_style" && isHotspotsTable));
                        const clearLabel =
                          selectedCell?.fieldName === "panorama_file"
                            ? "Clear linked panorama"
                            : selectedCell?.fieldName === "hotspot_style"
                              ? "Clear hotspot style"
                              : "Clear name";
                        const rowContent = (
                          <TableRow
                            key={rowKey}
                            className="border-gray-700"
                            onContextMenu={(event) => {
                              if (!isHotspotsActive) return;
                              if (isRowSelected) return;
                              handleSelectRow(event, index, row);
                            }}
                          >
                            <TableCell className="text-[11px] text-gray-500">
                              {index + 1}
                            </TableCell>
                            {displayedFields.map((field: FieldStructure) => {
                              const isSelected =
                                selectedCell?.rowIndex === index &&
                                selectedCell?.fieldName === field.name;
                              const customWidth = columnWidths[field.name];
                              const widthValue =
                                customWidth ?? getDefaultColumnWidth(field, activeRecord?.name);
                              const widthStyle = { width: widthValue, minWidth: widthValue };
                              const isLinkedPanoramaCell =
                                field.fieldType === "panorama_file" && isHotspotsTable;
                              const isEditing =
                                editingCell?.rowIndex === index &&
                                editingCell?.fieldName === field.name;
                              const isHotspotNameCell =
                                isHotspotsTable && field.name === "name";
                              const isHotspotStyleCell =
                                isHotspotsTable && field.name === "hotspot_style";
                              const isCustomCell = isCustomField(field, activeRecord?.name);
                              const isCustomEditableCell =
                                (isHotspotsTable || isPanoramasTable) && isCustomCell;
                              const isEditableCell =
                                (field.name === "name" &&
                                  (isHotspotsTable || isPanoramasTable)) ||
                                (field.name === "hotspot_style" && isHotspotsTable);
                              const prevRowSelected =
                                index > 0 &&
                                selectedRowKeys.has(getRowSelectionKey(activeRows[index - 1], index - 1));
                              const nextRowSelected =
                                index < activeRows.length - 1 &&
                                selectedRowKeys.has(getRowSelectionKey(activeRows[index + 1], index + 1));
                              const showNameSelection =
                                isHotspotNameCell &&
                                (isSelected ||
                                  (isRowSelected &&
                                    (!selectedCell || selectedCell.fieldName === "name")));
                              const showLinkedPanoramaSelection =
                                isLinkedPanoramaCell &&
                                (isSelected ||
                                  (isRowSelected && selectedCell?.fieldName === "panorama_file"));
                              const showHotspotStyleSelection =
                                isHotspotStyleCell &&
                                (isSelected ||
                                  (isRowSelected && selectedCell?.fieldName === "hotspot_style"));
                              const showCustomCellSelection =
                                isCustomEditableCell &&
                                (isSelected ||
                                  (isRowSelected &&
                                    selectedCell?.fieldName === field.name));
                              const showSelection =
                                showNameSelection ||
                                showLinkedPanoramaSelection ||
                                showHotspotStyleSelection ||
                                showCustomCellSelection;
                              const selectionClass = isEditing
                                ? "bg-transparent ring-0"
                                : showSelection
                                  ? "bg-blue-500/10"
                                  : "";
                              const selectionOutlineClass = showNameSelection
                                ? `border-blue-400 border-l border-r ${prevRowSelected ? "border-t-0" : "border-t"} ${
                                    nextRowSelected ? "border-b-0" : "border-b"
                                  }`
                                : showLinkedPanoramaSelection || showHotspotStyleSelection || showCustomCellSelection
                                  ? "border border-blue-400"
                                  : "";
                              const isDraftPanoramaCell =
                                isHotspotsActive &&
                                field.fieldType === "panorama_file" &&
                                isDraftRow(row);
                              return (
                                <TableCell
                                  key={field.name}
                                  style={widthStyle}
                                  className={`text-xs text-gray-200 whitespace-nowrap truncate ${selectionClass} ${selectionOutlineClass} ${
                                    isHotspotStyleCell || isHotspotNameCell || isCustomEditableCell ? "cursor-text" : ""
                                  } ${isDraftPanoramaCell ? "relative group" : ""} ${
                                    isLinkedPanoramaCell ? "p-0" : ""
                                  }`}
                                  onMouseDown={(event) => {
                                    if (isHotspotNameCell) {
                                      if (event.shiftKey || event.metaKey || event.ctrlKey) {
                                        event.preventDefault();
                                      }
                                      return;
                                    }
                                    if ((isHotspotStyleCell || isCustomEditableCell) && !isEditing) {
                                      event.preventDefault();
                                    }
                                  }}
                                  onClick={(event) => {
                                    const wasSelected =
                                      selectedCell?.rowIndex === index &&
                                      selectedCell?.fieldName === field.name;
                                    handleSelectRow(event, index, row);
                                    setSelectedCell({ rowIndex: index, fieldName: field.name });
                                    setHotspotStylePopoverRow(
                                      isHotspotStyleCell && !isEditing && wasSelected ? index : null,
                                    );
                                  }}
                                  onDoubleClick={() => {
                                    if (!isEditableCell && !isCustomEditableCell) return;
                                    const currentValue = row[field.name];
                                    const valueString =
                                      typeof currentValue === "string"
                                        ? currentValue
                                        : currentValue === null || currentValue === undefined
                                          ? ""
                                          : String(currentValue);
                                    setEditingCell({
                                      rowIndex: index,
                                      fieldName: field.name,
                                      value: valueString,
                                    });
                                  }}
                                >
                                  {isEditing && isEditableCell ? (
                                    <input
                                      autoFocus
                                      value={editingCell.value}
                                      onChange={(event) =>
                                        setEditingCell((prev) =>
                                          prev ? { ...prev, value: event.target.value } : prev,
                                        )
                                      }
                                      onPaste={(event) => {
                                        if (!isHotspotsTable) return;
                                        if (field.name !== "name") return;
                                        const isLastRow = index === activeRows.length - 1;
                                        if (!isLastRow) return;
                                        const text = event.clipboardData.getData("text");
                                        const lines = text
                                          .split(/\r?\n/)
                                          .map((line) => line.trim())
                                          .filter(Boolean);
                                        if (lines.length <= 1) return;
                                        event.preventDefault();
                                        const [first, ...rest] = lines;
                                        setEditingCell((prev) =>
                                          prev ? { ...prev, value: first } : prev,
                                        );
                                        insertDraftHotspotRows(index, rest);
                                      }}
                                      onBlur={(event) =>
                                        commitNameEditPanel(event.currentTarget.value)
                                      }
                                      onKeyDown={(event) => {
                                        if (event.key === "Enter") {
                                          event.preventDefault();
                                          commitNameEditPanel(event.currentTarget.value);
                                        }
                                        if (event.key === "Escape") {
                                          event.preventDefault();
                                          setEditingCell(null);
                                        }
                                      }}
                                      className="w-full max-w-full min-w-0 bg-transparent border-none p-0 text-xs text-white outline-none focus:outline-none focus:ring-0"
                                    />
                                  ) : isEditing && isCustomEditableCell ? (
                                    field.fieldType === "longtext" ? (
                                      <Textarea
                                        autoFocus
                                        value={editingCell.value}
                                        onChange={(event) =>
                                          setEditingCell((prev) =>
                                            prev ? { ...prev, value: event.target.value } : prev,
                                          )
                                        }
                                        onBlur={(event) => {
                                          void commitCustomFieldEdit(
                                            index,
                                            field,
                                            event.currentTarget.value,
                                          );
                                          setEditingCell(null);
                                        }}
                                        className="w-full bg-gray-700 border-gray-600 text-white text-xs h-20"
                                      />
                                    ) : field.fieldType === "switch" ? (
                                      <input
                                        type="checkbox"
                                        checked={editingCell.value === "true"}
                                        onChange={(event) =>
                                          setEditingCell((prev) =>
                                            prev
                                              ? { ...prev, value: event.target.checked ? "true" : "false" }
                                              : prev,
                                          )
                                        }
                                        onPaste={(event) => {
                                          const text = event.clipboardData.getData("text");
                                          const lines = text
                                            .split(/\r?\n/)
                                            .map((l) => l.trim())
                                            .filter(Boolean);
                                          if (lines.length <= 1) return;
                                          event.preventDefault();
                                          void commitMultiLinePaste(index, field, lines);
                                        }}
                                        onBlur={() => {
                                          void commitCustomFieldEdit(
                                            index,
                                            field,
                                            editingCell.value,
                                          );
                                          setEditingCell(null);
                                        }}
                                        className="rounded border-gray-600 bg-gray-700"
                                      />
                                    ) : field.fieldType === "singleselect" ? (
                                      <select
                                        autoFocus
                                        value={editingCell.value}
                                        onChange={(event) => {
                                          void commitCustomFieldEdit(index, field, event.target.value);
                                          setEditingCell(null);
                                        }}
                                        onBlur={() => setEditingCell(null)}
                                        className="w-full bg-gray-700 border-gray-600 text-white text-xs rounded px-1 py-1"
                                      >
                                        <option value="">— None —</option>
                                        {(field.options ?? []).map((opt) => (
                                          <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                      </select>
                                    ) : field.fieldType === "date" || field.fieldType === "datetime" ? (
                                      <DateFieldEditor
                                        value={editingCell.value}
                                        options={field.dateOptions}
                                        onCommit={(isoValue) => {
                                          void commitCustomFieldEdit(index, field, isoValue);
                                          setEditingCell(null);
                                        }}
                                        onCancel={() => setEditingCell(null)}
                                      />
                                    ) : (
                                      <Input
                                        autoFocus
                                        type={
                                          field.fieldType === "number" ||
                                          field.fieldType === "integer"
                                            ? "number"
                                            : "text"
                                        }
                                        value={editingCell.value}
                                        onChange={(event) =>
                                          setEditingCell((prev) =>
                                            prev ? { ...prev, value: event.target.value } : prev,
                                          )
                                        }
                                        onPaste={(event) => {
                                          const text = event.clipboardData.getData("text");
                                          const lines = text
                                            .split(/\r?\n/)
                                            .map((l) => l.trim())
                                            .filter(Boolean);
                                          if (lines.length <= 1) return;
                                          event.preventDefault();
                                          void commitMultiLinePaste(index, field, lines);
                                        }}
                                        onBlur={(event) => {
                                          void commitCustomFieldEdit(
                                            index,
                                            field,
                                            event.currentTarget.value,
                                          );
                                          setEditingCell(null);
                                        }}
                                        onKeyDown={(event) => {
                                          if (event.key === "Enter") {
                                            event.preventDefault();
                                            void commitCustomFieldEdit(
                                              index,
                                              field,
                                              event.currentTarget.value,
                                            );
                                            setEditingCell(null);
                                          }
                                          if (event.key === "Escape") {
                                            event.preventDefault();
                                            setEditingCell(null);
                                          }
                                        }}
                                        className="bg-gray-700 border-gray-600 text-white text-xs h-8"
                                      />
                                    )
                                  ) : field.fieldType === "panorama_file" ? (
                                    (() => {
                                      const value = row[field.name];
                                      const url = typeof value === "string" ? value : "";
                                      if (isPanoramasTable) {
                                        if (!url) return null;
                                        return (
                                          <img
                                            src={url}
                                            alt=""
                                            className="h-8 w-14 rounded object-cover border border-gray-700"
                                            loading="lazy"
                                          />
                                        );
                                      }
                                      const hotspotId = String(
                                        (row as { id?: unknown }).id ?? "",
                                      );
                                      const panoramaId = String(
                                        (row as { panorama_id?: unknown }).panorama_id ?? "",
                                      );
                                      const isDraft = isDraftRow(row);
                                      const panoramaName =
                                        activeRecord?.name === HOTSPOTS_COLLECTION_NAME ||
                                        activeRecord?.name === LEGACY_HOTSPOTS_COLLECTION_NAME
                                          ? formatCellValue(row.panorama_name)
                                          : null;
                                      const canOpenInEditor = Boolean(hotspotId) && !isDraft;
                                      const canOpenInCMS = Boolean(panoramaId);
                                      return (
                                        <ContextMenu>
                                          <ContextMenuTrigger asChild>
                                            <button
                                              type="button"
                                              onClick={(event) => {
                                                event.preventDefault();
                                                event.stopPropagation();
                                                if (event.shiftKey || event.metaKey || event.ctrlKey) {
                                                  handleSelectRow(event, index, row);
                                                  setSelectedCell({
                                                    rowIndex: index,
                                                    fieldName: field.name,
                                                  });
                                                  return;
                                                }
                                                if (!isSelected) {
                                                  handleSelectRow(event, index, row);
                                                  setSelectedCell({
                                                    rowIndex: index,
                                                    fieldName: field.name,
                                                  });
                                                  return;
                                                }
                                                event.currentTarget.dispatchEvent(
                                                  new MouseEvent("contextmenu", {
                                                    bubbles: true,
                                                    clientX: event.clientX,
                                                    clientY: event.clientY,
                                                  }),
                                                );
                                              }}
                                              onContextMenu={(event) => {
                                                if (!isSelected) {
                                                  event.preventDefault();
                                                  event.stopPropagation();
                                                  handleSelectRow(event, index, row);
                                                  setSelectedCell({
                                                    rowIndex: index,
                                                    fieldName: field.name,
                                                  });
                                                  return;
                                                }
                                              }}
                                              className={`flex w-full items-center gap-2 px-3 py-2 text-left outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ${
                                                url ? "text-gray-200" : "text-gray-400"
                                              }`}
                                            >
                                              {url ? (
                                                <img
                                                  src={url}
                                                  alt=""
                                                  className="h-6 w-10 rounded object-cover border border-gray-700"
                                                  loading="lazy"
                                                />
                                              ) : (
                                                <div className="h-6 w-10 rounded bg-gray-700/60" />
                                              )}
                                              {panoramaName ? (
                                                <span>{panoramaName}</span>
                                              ) : (
                                                <span className="opacity-70">Link a panorama</span>
                                              )}
                                            </button>
                                          </ContextMenuTrigger>
                                          <ContextMenuContent className="bg-gray-800 border-gray-700 text-gray-200">
                                            <ContextMenuItem
                                              onClick={() => clearCellValue(index, "panorama_file")}
                                              className="text-red-300 focus:text-red-200"
                                            >
                                              Clear link
                                            </ContextMenuItem>
                                            {canOpenInCMS ? (
                                              <ContextMenuItem
                                                onClick={() => handleOpenPanoramaInCMS(panoramaId)}
                                              >
                                                Open
                                              </ContextMenuItem>
                                            ) : null}
                                            <ContextMenuSub>
                                              <ContextMenuSubTrigger>
                                                Link to panorama
                                              </ContextMenuSubTrigger>
                                              <ContextMenuSubContent className="bg-gray-800 border-gray-700 text-gray-200">
                                                {panoramas && panoramas.length > 0 ? (
                                                  panoramas.map((panorama) => {
                                                    const thumb =
                                                      panorama.thumbnail_url_webp ||
                                                      panorama.thumbnail_url ||
                                                      "";
                                                    return (
                                                      <ContextMenuItem
                                                        key={panorama.id}
                                                        onClick={() =>
                                                          handleLinkDraftPanorama(index, panorama)
                                                        }
                                                        className="gap-2 px-1 py-1"
                                                      >
                                                        {thumb ? (
                                                          <img
                                                            src={thumb}
                                                            alt=""
                                                            className="h-8 w-14 rounded object-cover border border-gray-700"
                                                            loading="lazy"
                                                          />
                                                        ) : (
                                                          <div className="h-8 w-14 rounded bg-gray-700/60" />
                                                        )}
                                                        <span>{panorama.name}</span>
                                                      </ContextMenuItem>
                                                    );
                                                  })
                                                ) : (
                                                  <ContextMenuItem
                                                    disabled
                                                    className="text-gray-500"
                                                  >
                                                    No panoramas available
                                                  </ContextMenuItem>
                                                )}
                                              </ContextMenuSubContent>
                                            </ContextMenuSub>
                                            {canOpenInEditor ? (
                                              <ContextMenuItem
                                                onClick={() => handleOpenHotspotInEditor(row)}
                                              >
                                                View in Editor
                                              </ContextMenuItem>
                                            ) : null}
                                          </ContextMenuContent>
                                        </ContextMenu>
                                      );
                                    })()
                                  ) : field.name === "content" ? (
                                    (() => {
                                      const value = row[field.name];
                                      if (value === null || value === undefined || value === "") {
                                        return "";
                                      }
                                      const fullValue =
                                        typeof value === "string"
                                          ? value
                                          : JSON.stringify(value, null, 2);
                                      const isOpen =
                                        contentPopover?.rowIndex === index &&
                                        contentPopover?.fieldName === field.name;
                                      return (
                                        <Popover
                                          open={isOpen}
                                          onOpenChange={(open) =>
                                            setContentPopover(
                                              open ? { rowIndex: index, fieldName: field.name } : null,
                                            )
                                          }
                                        >
                                          <PopoverTrigger asChild>
                                            <button
                                              type="button"
                                              className="w-full text-left text-xs text-gray-200"
                                            >
                                              {formatCellValue(value)}
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent
                                            side="top"
                                            align="start"
                                            className="w-[520px] max-w-[80vw] max-h-[60vh] overflow-auto bg-gray-800 border-gray-700 text-gray-200"
                                          >
                                            <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed">
                                              {fullValue}
                                            </pre>
                                          </PopoverContent>
                                        </Popover>
                                      );
                                    })()
                                  ) : field.fieldType === "longtext" ? (
                                    (() => {
                                      const value = row[field.name];
                                      if (value === null || value === undefined || value === "") {
                                        return "";
                                      }
                                      const fullValue =
                                        typeof value === "string"
                                          ? value
                                          : JSON.stringify(value, null, 2);
                                      const isOpen =
                                        contentPopover?.rowIndex === index &&
                                        contentPopover?.fieldName === field.name;
                                      return (
                                        <Popover
                                          open={isOpen}
                                          onOpenChange={(open) =>
                                            setContentPopover(
                                              open ? { rowIndex: index, fieldName: field.name } : null,
                                            )
                                          }
                                        >
                                          <PopoverTrigger asChild>
                                            <button
                                              type="button"
                                              className="w-full text-left text-xs text-gray-200"
                                            >
                                              {formatCellValue(value)}
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent
                                            side="top"
                                            align="start"
                                            className="w-[520px] max-w-[80vw] max-h-[60vh] overflow-auto bg-gray-800 border-gray-700 text-gray-200"
                                          >
                                            <pre className="whitespace-pre-wrap break-words text-xs leading-relaxed">
                                              {fullValue}
                                            </pre>
                                          </PopoverContent>
                                        </Popover>
                                      );
                                    })()
                                  ) : isHotspotStyleCell ? (
                                    (() => {
                                      const value = row[field.name];
                                      const id = typeof value === "string" ? value : "";
                                      const styleName = id ? hotspotStyleNameById.get(id) ?? id : "";
                                      const hotspotType = typeof row.type === "string" ? row.type : "";
                                      const compatiblePresets = hotspotStylePresets.filter(
                                        (preset) => preset.hotspotType === hotspotType,
                                      );
                                      const isPopoverOpen =
                                        isSelected && hotspotStylePopoverRow === index && !isEditing;
                                      if (!isPopoverOpen) {
                                        return styleName || "";
                                      }
                                      return (
                                        <Popover
                                          open={isPopoverOpen}
                                          onOpenChange={(open) => {
                                            if (!open) {
                                              setHotspotStylePopoverRow(null);
                                            }
                                          }}
                                        >
                                          <PopoverTrigger asChild>
                                            <button
                                              type="button"
                                              className="flex w-full items-center justify-between gap-2 text-left text-xs text-gray-200"
                                              onMouseDown={(event) => {
                                                event.preventDefault();
                                              }}
                                            >
                                              <span className={styleName ? "" : "text-gray-400"}>
                                                {styleName || "Select hotspot style"}
                                              </span>
                                              <ChevronDown size={12} className="text-gray-400" />
                                            </button>
                                          </PopoverTrigger>
                                          <PopoverContent
                                            side="bottom"
                                            align="start"
                                            className="w-64 max-w-[60vw] p-1 bg-gray-800 border-gray-700 text-gray-200"
                                          >
                                            <div className="max-h-64 overflow-auto">
                                              {id ? (
                                                <>
                                                  <button
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left text-xs text-gray-300 hover:bg-gray-700"
                                                    onMouseDown={(event) => {
                                                      event.preventDefault();
                                                    }}
                                                    onClick={() => {
                                                      const targetIndices =
                                                        getHotspotStyleTargetIndices(index);
                                                      suppressHotspotRefreshRef.current = true;
                                                      void (async () => {
                                                        try {
                                                          await applyHotspotStyleToRows(
                                                            targetIndices,
                                                            "",
                                                          );
                                                        } finally {
                                                          suppressHotspotRefreshRef.current = false;
                                                        }
                                                      })();
                                                      setHotspotStylePopoverRow(null);
                                                    }}
                                                  >
                                                    Clear style
                                                  </button>
                                                  {compatiblePresets.length > 0 && (
                                                    <div className="mx-2 my-1 border-t border-gray-700" />
                                                  )}
                                                </>
                                              ) : null}
                                              {compatiblePresets.length === 0 ? (
                                                <div className="px-3 py-2 text-xs text-gray-500">
                                                  No hotspot styles available
                                                </div>
                                              ) : (
                                                compatiblePresets.map((preset) => (
                                                  <button
                                                    key={preset.id}
                                                    type="button"
                                                    className="w-full px-3 py-2 text-left text-xs text-gray-200 hover:bg-gray-700"
                                                    onMouseDown={(event) => {
                                                      event.preventDefault();
                                                    }}
                                                    onClick={() => {
                                                      const targetIndices =
                                                        getHotspotStyleTargetIndices(index);
                                                      suppressHotspotRefreshRef.current = true;
                                                      void (async () => {
                                                        try {
                                                          await applyHotspotStyleToRows(
                                                            targetIndices,
                                                            preset.id,
                                                          );
                                                        } finally {
                                                          suppressHotspotRefreshRef.current = false;
                                                        }
                                                      })();
                                                      setHotspotStylePopoverRow(null);
                                                    }}
                                                  >
                                                    {preset.name}
                                                  </button>
                                                ))
                                              )}
                                            </div>
                                          </PopoverContent>
                                        </Popover>
                                      );
                                    })()
                                  ) : (field.fieldType === "date" || field.fieldType === "datetime") ? (
                                    (() => {
                                      const value = row[field.name];
                                      if (value === null || value === undefined || value === "") return "";
                                      return formatDateValue(value, field.dateOptions) || formatCellValue(value);
                                    })()
                                  ) : (
                                    formatCellValue(row[field.name])
                                  )}
                                </TableCell>
                              );
                            })}
                            {showAddFieldColumn && (
                              <TableCell className="text-xs text-gray-500" />
                            )}
                          </TableRow>
                        );

                        if (!isHotspotsActive) {
                          return rowContent;
                        }

                        return (
                          <ContextMenu key={rowKey}>
                            <ContextMenuTrigger asChild>{rowContent}</ContextMenuTrigger>
                            <ContextMenuContent className="bg-gray-800 border-gray-700 text-gray-200">
                              {canClearCell && selectedCell ? (
                                <ContextMenuItem
                                  onClick={() =>
                                    clearCellValue(
                                      selectedCell.rowIndex,
                                      selectedCell.fieldName as "name" | "panorama_file" | "hotspot_style",
                                    )
                                  }
                                >
                                  {clearLabel}
                                </ContextMenuItem>
                              ) : null}
                              {selectedRowKeys.size > 1 ? (
                                <ContextMenuItem
                                  className="text-red-400 focus:text-red-300"
                                  onClick={handleDeleteSelectedHotspots}
                                >
                                  Delete selected hotspots ({selectedRowKeys.size})
                                </ContextMenuItem>
                              ) : (
                                <ContextMenuItem
                                  className="text-red-400 focus:text-red-300"
                                  onClick={() => handleDeleteHotspotRow(index, row)}
                                >
                                  Delete hotspot
                                </ContextMenuItem>
                              )}
                            </ContextMenuContent>
                          </ContextMenu>
                        );
                      })
                    )}
                      {isHotspotsActive && (
                      <TableRow
                        className={`hover:bg-muted/5 cursor-pointer ${activeRows.length > 0 ? "border-gray-700" : "border-transparent"}`}
                        onClick={() => {
                          insertDraftHotspotRow(activeRows.length);
                        }}
                      >
                        <TableCell className="text-[11px] text-gray-500 min-w-0">
                          <Plus className="h-3 w-3 text-gray-400" />
                        </TableCell>
                        {displayedFields.map((field: FieldStructure) => (
                          <TableCell key={field.name} className="text-xs text-gray-200 min-w-[140px]">
                            {field.name === "name" ? (
                              <span className="text-gray-500">Add hotspot</span>
                            ) : null}
                          </TableCell>
                        ))}
                        {showAddFieldColumn && <TableCell className="text-xs text-gray-500" />}
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Data Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white">{selectedRecord?.name} - Data Preview</DialogTitle>
            <DialogDescription className="text-gray-400">
              Showing first 10 items of {selectedRecord?.rowCount} total items
            </DialogDescription>
          </DialogHeader>

          {selectedRecord?.data && (
            <div className="overflow-auto">
              <div className="mb-4">
                <h4 className="text-white font-medium mb-2">Field Structure</h4>
                <div className="flex flex-wrap gap-2">
                  {selectedRecord.fieldStructure.map((field: FieldStructure) => (
                    <Badge key={field.name} className={getFieldTypeColor(field.fieldType)}>
                      {field.displayName} ({field.fieldType})
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-600 hover:bg-transparent">
                      {selectedRecord.fieldStructure.map((field: FieldStructure) => (
                        <TableHead
                          key={field.name}
                          className="text-gray-300 transition-colors hover:bg-muted/5"
                        >
                          {getFieldLabel(field, selectedRecord?.name)}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedRecord.data?.rows || [])
                      .slice(0, 10)
                      .map((row: DataRow, index: number) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: Stable list of first 10 items in preview
                        <TableRow key={index} className="border-gray-600">
                          {selectedRecord.fieldStructure.map((field: FieldStructure) => (
                            <TableCell key={field.name} className="text-gray-300">
                              <div className="max-w-xs truncate">
                                {(() => {
                                  const value = row[field.name];
                                  if (value === null || value === undefined || value === "") {
                                    return "-";
                                  }
                                  if (field.fieldType === "date" || field.fieldType === "datetime") {
                                    const formatted = formatDateValue(value, field.dateOptions);
                                    return formatted || value;
                                  }
                                  return value;
                                })()}
                              </div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </div>

              {(selectedRecord.data?.rows || []).length > 10 && (
                <p className="text-gray-400 text-sm mt-2 text-center">
                  ... and {(selectedRecord.data?.rows || []).length - 10} more items
                </p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Update Dataset Dialog */}
      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Update Dataset
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Update "{updateRecord?.name}" with new CSV data
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Merge Field Selection */}
            <div>
              <Label className="text-gray-300 text-sm">Merge Field</Label>
              <select
                value={mergeField}
                onChange={(e) => setMergeField(e.target.value)}
                className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select field to match records...</option>
                {updateRecord?.fieldStructure?.map((field: FieldStructure) => (
                  <option key={field.name} value={field.name}>
                    {field.displayName || field.name} ({field.fieldType})
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Records with matching values in this field will be updated
              </p>
            </div>

            {/* Update Options */}
            <div className="space-y-2">
              <Label className="text-gray-300 text-sm">Update Options</Label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={updateExisting}
                    onChange={(e) => setUpdateExisting(e.target.checked)}
                    className="rounded border-gray-600 bg-gray-700 text-blue-400"
                  />
                  <span className="text-sm text-gray-300">Update existing records</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={addNew}
                    onChange={(e) => setAddNew(e.target.checked)}
                    className="rounded border-gray-600 bg-gray-700 text-blue-400"
                  />
                  <span className="text-sm text-gray-300">Add new records</span>
                </label>
              </div>
            </div>

            {/* File Upload */}
            <div>
              <Label className="text-gray-300 text-sm">New CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                ref={updateFileInputRef}
                onChange={handleUpdateDataset}
                disabled={isUploading}
                className="bg-gray-700 border-gray-600 text-white file:bg-gray-600 file:border-gray-500 file:text-white text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Upload a new CSV file to update the dataset
              </p>
            </div>

            {isUploading && (
              <div className="flex items-center gap-2 text-blue-400">
                <div className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm">Updating dataset...</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={editFieldDialogOpen} onOpenChange={setEditFieldDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Edit Field: {editingField?.displayName ?? editingField?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Configure options for this field.
            </DialogDescription>
          </DialogHeader>
          {editingField && editingField.fieldType === "singleselect" && (
            <div className="space-y-4">
              {/* Field name (read-only) */}
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-1">Field</p>
                <p className="text-sm text-white font-medium">{editingField.displayName || editingField.name}</p>
              </div>
              {/* Options */}
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-2">Options</p>
                <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
                  {editFieldOptions.length === 0 && (
                    <p className="text-xs text-gray-500 text-center py-2">No options defined</p>
                  )}
                  {editFieldOptions.map((opt) => (
                    <div key={opt} className="flex items-center gap-2 group">
                      <span className="flex-1 text-xs text-gray-100 bg-gray-700 px-3 py-1.5 rounded-full border border-gray-600">
                        {opt}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setEditFieldOptions((prev) => prev.filter((o) => o !== opt));
                          if (editFieldDefaultValue === opt) setEditFieldDefaultValue("");
                        }}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-400 transition-opacity"
                        title="Remove option"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
                {/* Add option input */}
                <div className="flex items-center gap-1 mt-2">
                  <input
                    type="text"
                    value={editFieldOptionInput}
                    onChange={(e) => setEditFieldOptionInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && editFieldOptionInput.trim()) {
                        e.preventDefault();
                        const val = editFieldOptionInput.trim();
                        if (!editFieldOptions.includes(val)) {
                          setEditFieldOptions((prev) => [...prev, val]);
                        }
                        setEditFieldOptionInput("");
                      }
                    }}
                    placeholder="Add an option..."
                    className="flex-1 px-3 py-1.5 text-xs bg-gray-700 border border-gray-600 text-white rounded-full focus:outline-none focus:border-gray-400 placeholder-gray-500"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = editFieldOptionInput.trim();
                      if (val && !editFieldOptions.includes(val)) {
                        setEditFieldOptions((prev) => [...prev, val]);
                      }
                      setEditFieldOptionInput("");
                    }}
                    className="text-xs text-gray-400 hover:text-white px-1 transition-colors"
                    title="Add option"
                  >
                    <Plus size={14} />
                  </button>
                </div>
              </div>
              {/* Default value */}
              <div>
                <p className="text-[11px] text-gray-400 uppercase tracking-widest mb-2">Default</p>
                <select
                  value={editFieldDefaultValue}
                  onChange={(e) => setEditFieldDefaultValue(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 text-white rounded-md px-3 py-1.5 text-xs focus:outline-none"
                >
                  <option value="">— None —</option>
                  {editFieldOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  onClick={() => setEditFieldDialogOpen(false)}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => void handleEditFieldSave()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save
                </Button>
              </div>
            </div>
          )}
          {editingField && (editingField.fieldType === "date" || editingField.fieldType === "datetime") && (
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300 text-sm">Date format</Label>
                <select
                  value={editFieldDateOptions.dateFormat}
                  onChange={(e) => {
                    const fmt = e.target.value as DateFieldOptions["dateFormat"];
                    setEditFieldDateOptions((prev) => ({
                      ...prev,
                      dateFormat: fmt,
                      timeFormat: fmt === "us" ? "12h" : "24h",
                    }));
                  }
                  }
                  className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
                >
                  <option value="local">
                    Local ({new Date().toLocaleDateString()})
                  </option>
                  <option value="friendly">
                    Friendly ({`${new Date().getDate()} ${new Date().toLocaleString("en-US", { month: "long" })} ${new Date().getFullYear()}`})
                  </option>
                  <option value="us">
                    US ({`${new Date().getMonth() + 1}/${new Date().getDate()}/${new Date().getFullYear()}`})
                  </option>
                  <option value="european">
                    European ({`${new Date().getDate()}/${new Date().getMonth() + 1}/${new Date().getFullYear()}`})
                  </option>
                  <option value="iso">
                    ISO ({new Date().toISOString().slice(0, 10)})
                  </option>
                </select>
              </div>
              <div className="flex items-center justify-between">
                <Label className="text-gray-300 text-sm">Include time</Label>
                <Switch
                  checked={editFieldDateOptions.includeTime}
                  onCheckedChange={(checked) =>
                    setEditFieldDateOptions((prev) => ({
                      ...prev,
                      includeTime: checked,
                    }))
                  }
                />
              </div>
              {editFieldDateOptions.includeTime && (
                <>
                  <div>
                    <Label className="text-gray-300 text-sm">Time format</Label>
                    <select
                      value={editFieldDateOptions.timeFormat}
                      onChange={(e) =>
                        setEditFieldDateOptions((prev) => ({
                          ...prev,
                          timeFormat: e.target.value as "12h" | "24h",
                        }))
                      }
                      className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
                    >
                      <option value="12h">
                        12 hour ({new Date().toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })})
                      </option>
                      <option value="24h">
                        24 hour ({`${String(new Date().getHours()).padStart(2, "0")}:${String(new Date().getMinutes()).padStart(2, "0")}`})
                      </option>
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300 text-sm">
                      Use the same time zone for all collaborators
                    </Label>
                    <Switch
                      checked={editFieldDateOptions.useSharedTimezone}
                      onCheckedChange={(checked) =>
                        setEditFieldDateOptions((prev) => ({
                          ...prev,
                          useSharedTimezone: checked,
                        }))
                      }
                    />
                  </div>
                  {editFieldDateOptions.useSharedTimezone && (
                    <div>
                      <Label className="text-gray-300 text-sm">Time zone</Label>
                      <select
                        value={editFieldDateOptions.timezone}
                        onChange={(e) =>
                          setEditFieldDateOptions((prev) => ({
                            ...prev,
                            timezone: e.target.value,
                          }))
                        }
                        className="w-full bg-gray-700 border-gray-600 text-white rounded-md px-3 py-2 text-sm"
                      >
                        <option value="GMT/UTC">GMT/UTC</option>
                        <option value="America/New_York">America/New_York (ET)</option>
                        <option value="America/Chicago">America/Chicago (CT)</option>
                        <option value="America/Denver">America/Denver (MT)</option>
                        <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                        <option value="Europe/London">Europe/London (GMT/BST)</option>
                        <option value="Europe/Paris">Europe/Paris (CET)</option>
                        <option value="Europe/Berlin">Europe/Berlin (CET)</option>
                        <option value="Europe/Madrid">Europe/Madrid (CET)</option>
                        <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
                        <option value="Asia/Shanghai">Asia/Shanghai (CST)</option>
                        <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                        <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                      </select>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <Label className="text-gray-300 text-sm">Display time zone</Label>
                    <Switch
                      checked={editFieldDateOptions.displayTimezone}
                      onCheckedChange={(checked) =>
                        setEditFieldDateOptions((prev) => ({
                          ...prev,
                          displayTimezone: checked,
                        }))
                      }
                    />
                  </div>
                </>
              )}
              <div className="flex items-center justify-between">
                <Label className="text-gray-300 text-sm">
                  Default to{" "}
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-gray-700 text-gray-200 text-xs">
                    <CalendarIcon className="h-3 w-3" /> Current date
                  </span>
                </Label>
                <Switch
                  checked={editFieldDateOptions.defaultToCurrentDate}
                  onCheckedChange={(checked) =>
                    setEditFieldDateOptions((prev) => ({
                      ...prev,
                      defaultToCurrentDate: checked,
                    }))
                  }
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => setEditFieldDialogOpen(false)}
                  className="bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => void handleEditFieldSave()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Save
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
